import   React                      ,
       { useState                   ,
         useRef                     ,
         useEffect                  }  from 'react';
import { createPortal              }  from 'react-dom';

import   classNames                    from 'classnames';

import   styles                        from './VideoSheet2.module.scss';

import { useTypedSelector           }  from '../../../store/useTypeSelector';
import { RootState                  }  from '../../../store/generalReducer';

import   Field                         from '../../widget/inputs/Field';
import   FieldMedia                    from '../../widget/inputs/FieldMedia';
import   FieldCalendar                 from '../../widget/inputs/FieldCalendar';
import   TextArea                      from '../../widget/inputs/TextArea';
import   Select                        from '../../widget/inputs/Select';
import   Toggle                        from '../../widget/inputs/Toggle';
import   Trigger                       from '../../widget/actuators/Trigger';
import   WidgetFrame                   from '../../widget/navigation/WidgetFrame';
import   Window                        from '../../widget/windows/window';
import   Box                           from '../../widget/windows/Box';
import   CollapsiblePanel              from '../../widget/windows/CollapsiblePanel';
import   TagInput                      from '../../widget/dataDisplay/tags/TagInput';
import   Label                         from '../../widget/dataDisplay/labels/Label';
import   CreationIp                    from '../../widget/dataDisplay/labels/CreationIp';
import   Icon                          from '../../widget/images/Icon';
import   AnimatedWaitingIcon2          from '../../widget/animations/animatedWaitingIcon2';
import   VideoResolutionSelector       from './VideoResolutionSelector';
import   { MediaGuideState }           from './MediaGuideBanner';

import { UserStatus                 }  from '../../../dataTypes/userManagment/types';
import { CHAT_MODERATION_LEVELS     }  from '../../../dataTypes/moderationManagment/chat_types';

import { DocumentWithState          }  from '../../../dataTypes/documentManagment/types';

import { VideoCategory              ,
         Video                      ,
         initializedVideo           }  from '../../../dataTypes/mediaManagment/video_types';

import { Keyword                    }  from '../../../dataTypes/keywordManagment/types';

import { testTitle                  ,
         testReleaseDate            ,
         testFirstUseDate           ,
         testImageFileToUpload      ,
         testVideoFileToUpload      ,
         testVideoUrl               ,
         testRequiredUserStatusId   ,
         testCategoriesIds          }  from '../../../dataTypes/mediaManagment/video_validators';

import { Product                    ,
         ProductVariant             ,
         ProductPrice               }  from '../../../dataTypes/productManagment/types';

import { convertStringToDate        ,
         convertDateToString        ,
         convertTimeToSeconds       ,
         formatDurationFromIso      }  from '../../../dataTypes/timeManagment/utils';

import { CompressionLevel           ,
         DEFAULT_COMPRESSION_LEVEL  ,
         HlsResolutionId            ,
         isSource4K                 }  from '../../../dataTypes/mediaManagment/hlsResolutionProfiles';

import { WWW_URLS                   }  from '../../../backendServices/urls';

import { getKeywords                ,
         createKeyword              }  from '../../../backendServices/keywordManagment/api';

import { getUserStatusList          }  from '../../../backendServices/userManagment/api';
import { updateVideo                ,
         createVideo                }  from '../../../backendServices/mediaManagment/api_video';
import { getMediaCategories         }  from '../../../backendServices/mediaManagment/api_category';
import { getInformationsFromYoutube }  from '../../../backendServices/videoManagment/api_youtube';
import { getProducts                ,
         createProduct              ,
         updateProduct              ,
         updateProductVisibility    }  from '../../../backendServices/productManagment/api_product';
import { refreshProductById       }  from '../../../store/productManagment/actions';

import   MediaCategoryManager          from "../mediaCategoryManagment/MediaCategoryManager";

import   DocumentList                  from "../documentManagment/DocumentList";

import { octetsRestants            }  from '../../../dataTypes/adminManagment/quota';
import { getActiveUploads           ,
         getUploadsByEntityId       }  from '../../../store/fileManagment/actions';
import { UploadStatus               }  from '../../../store/fileManagment/types';

import { createDocument             ,
         deleteDocuments            ,
         getDocumentsByEntityId     }  from '../../../backendServices/documentManagment/api';

import { listAdminSocialNetworks    ,
         getSocialNetworkAuthorizeUrl ,
         disconnectSocialNetwork     ,
         publishVideoToSocialNetwork ,
         listVideoSocialNetworkPublications ,
         AdminSocialNetworkStatus   ,
         SocialNetworkPublicationStatus ,
         SocialNetworkPrivacyStatus }  from '../../../backendServices/adminManagment/api_socialNetworks';

export interface VideoSheet2Props {
  video?                  : Video;
  /** Créateur de la vidéo, quand un autre compte ouvre sa fiche : catégories et produits lui appartiennent. */
  ownerAdminId?           : number;
  /** Création guidée (MediaSheet) : la fiche dit où en est sa saisie pour que l'accompagnement avance. */
  onGuideState?           : (state: MediaGuideState) => void;
  onCreate?               : (id: number) => void;
  onEdit?                 : (id: number) => void;
  uploadProgressFunction? : (progress: number) => void;
  // Élément fourni par MediaSheet, à droite du sélecteur de type : la barre
  // Annuler/Créer y est projetée pour tenir sur la même ligne que lui. Absent
  // (mode édition, fiche autonome), elle reste en bas du formulaire.
  actionsSlot?            : HTMLElement | null;
  // Son jumeau à gauche de la même ligne : le bandeau Id / Uuid / Date y est
  // projeté en édition, pour tenir sur la même ligne que les boutons plutôt que
  // d'occuper une ligne entière. Absent, il reste en tête du formulaire.
  metaSlot?               : HTMLElement | null;
  // Élément fourni par MediaSheet, dans la colonne 2 de la même ligne : le bloc
  // de récupération des informations Youtube y est projeté (création vidéo
  // uniquement). Absent, il reste à sa place d'origine dans le formulaire.
  youtubeRetrievalSlot?   : HTMLElement | null;
  /**
   * Publication d'un enregistrement de régie (RecordingStorageModal, bouton
   * "Publier") : la source vidéo est déjà sur le disque du serveur, jamais un
   * upload. Remplace le sélecteur de fichier vidéo par un encart lecture
   * seule, et fait sauter tout l'upload par morceaux à l'enregistrement — la
   * fiche passe directement en transcodage (voir createVideo, recordingSource).
   * Incompatible avec le mode édition (video prop) : toujours une création.
   */
  prefillFromRecording?   : {
    channelUuid    : string;
    sessionId      : number;
    filename       : string;
    format         : string;
    durationSeconds: number | null;
    /** Nombre d'AUTRES fichiers de la même prise multi-angle, prêts à être publiés comme angles. */
    angleCount?    : number;
    /** URL de visionnage déjà résolue par l'appelant (RecordingStorageModal
        connaît managerUrl/apiKey/channelUuid ; rien de tout ça n'est composé
        ici). Simple lecture : le fichier n'est jamais téléversé par ce
        chemin, il est déjà sur le disque du serveur. */
    previewUrl?    : string;
  };
}

// Les commentaires sont toujours triés par l'IA de Google (customer-api), comme le
// chat : reste à choisir le degré, avec les quatre niveaux de l'écran Modération
// (0 Légère, 1 Standard, 2 Avancée, 3 Manuelle). Plus de « modérés oui/non »
// (2026-10-08). Valeur enregistrée : Video.commentsModeration, -1 = fermés.
const commentsModerationChoices = CHAT_MODERATION_LEVELS.map(l => String(l.value));
const commentsModerationLabels  = CHAT_MODERATION_LEVELS.map(l => l.label);
const DEFAULT_COMMENTS_LEVEL    = '1';

// Statuts qui verrouillent le switch (déjà publié, ou publication en cours) :
// il n'y a pas de chemin pour "dépublier" depuis Kleon, republier après un
// Error reste possible.
const SOCIAL_NETWORK_LOCKED_STATUSES: ReadonlyArray<string> = ['New', 'Processing', 'Success'];

// Choix de visibilité propres à chaque réseau, par code — pour l'instant
// seul Youtube est implémenté ; un futur réseau aura les siens, pas
// forcément les mêmes (ex. pas de "non répertorié" partout).
const SOCIAL_NETWORK_PRIVACY_STATUS_CHOICES_BY_CODE: Record<string, { value: SocialNetworkPrivacyStatus; label: string }[]> = {
  youtube: [
    { value: 'private',  label: 'Privée'           },
    { value: 'unlisted', label: 'Non répertoriée'  },
    { value: 'public',   label: 'Publique'         },
  ],
  // Pas de "non répertorié" côté Facebook : une publication non "public"
  // reste un brouillon visible des seuls administrateurs de la Page (cf.
  // SocialNetworkPublisher_Facebook::publish()).
  facebook: [
    { value: 'private', label: 'Privée (brouillon)' },
    { value: 'public',  label: 'Publique'           },
  ],
  // Tant que l'app Tiktok de Kleon n'a pas passé l'audit "Content Posting
  // API", Tiktok force SELF_ONLY côté serveur quoi que ce choix envoie (cf.
  // SocialNetworkPublisher_TikTok::mapPrivacyStatus côté admin-api) — le
  // sélecteur reste affiché pour ne pas coder cette limitation temporaire en
  // dur côté front.
  tiktok: [
    { value: 'private', label: 'Privée (moi uniquement)' },
    { value: 'public',  label: 'Publique'                },
  ],
  // Une seule valeur possible : l'API Instagram n'a aucun équivalent
  // private/unlisted (cf. SocialNetworkPublisher_Instagram::publish(), qui
  // refuse explicitement tout ce qui n'est pas 'public').
  instagram: [
    { value: 'public', label: 'Publique' },
  ],
  // Vimeo honore nativement les trois niveaux, comme Youtube — seul autre
  // réseau dans ce cas (cf. SocialNetworkPublisher_Vimeo::mapPrivacyStatus).
  // "Privée"/"Non répertoriée" peuvent être refusées par Vimeo si le compte
  // connecté est en offre gratuite ; l'erreur Vimeo remonte telle quelle.
  vimeo: [
    { value: 'private',  label: 'Privée'          },
    { value: 'unlisted', label: 'Non répertoriée' },
    { value: 'public',   label: 'Publique'        },
  ],
  // Pas de "non répertorié" côté Dailymotion, comme Facebook : un choix
  // différent de "Publique" laisse la vidéo en brouillon, visible du seul
  // créateur (cf. SocialNetworkPublisher_Dailymotion::publish()).
  dailymotion: [
    { value: 'private', label: 'Privée (brouillon)' },
    { value: 'public',  label: 'Publique'           },
  ],
  // LinkedIn n'a ni "privé" ni "non répertorié" au sens Youtube : tout ce
  // qui n'est pas "Publique" est envoyé en CONNECTIONS, c'est-à-dire visible
  // des seules relations du créateur (cf.
  // SocialNetworkPublisher_LinkedIn::mapVisibility()).
  linkedin: [
    { value: 'private', label: 'Mes relations' },
    { value: 'public',  label: 'Publique'      },
  ],
};

// Logo affiché à la place du nom du réseau. Dailymotion n'en a pas : ce
// réseau est masqué dans la liste (cf. plus bas), sans logo à fournir.
const SOCIAL_NETWORK_LOGO_BY_CODE: Record<string, string> = {
  youtube  : '/images/socialNetworks/Logo_Youtube.png',
  facebook : '/images/socialNetworks/Logo_Facebook.png',
  instagram: '/images/socialNetworks/Logo_Instagram.png',
  tiktok   : '/images/socialNetworks/Logo_TikTok.png',
  vimeo    : '/images/socialNetworks/Logo_Vimeo.png',
  linkedin : '/images/socialNetworks/Logo_Linkedin.png',
};

export const VideoSheet2 = ({ video                  ,
                               ownerAdminId           ,
                               onCreate               ,
                               onEdit                 ,
                               uploadProgressFunction ,
                               actionsSlot            ,
                               metaSlot               ,
                               youtubeRetrievalSlot   ,
                               onGuideState           ,
                               prefillFromRecording   }: VideoSheet2Props) => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);

  const isEditMode = video !== undefined;
  const initialVideo: Video = isEditMode ? video! : initializedVideo();

  // Titre et durée pré-remplis depuis l'enregistrement — nom brut, sans retouche :
  // l'opérateur le modifie lui-même si besoin, comme pour tout autre champ de la fiche.
  const recordingDurationHms = (() => {
    const s = prefillFromRecording?.durationSeconds;
    if (s === null || s === undefined) return '';
    const total = Math.round(s);
    const pad   = (n: number) => String(n).padStart(2, '0');
    return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
  })();

  const [ publishAngles                    , setPublishAngles                    ] = useState(true);
  const [ youtubeIdForRetrieving           , setYoutubeIdForRetrieving           ] = useState("");

  const [ videoCategories                  , setVideoCategories                  ] = useState<VideoCategory[]>([]);

  const [ keywordsDictionary               , setKeywordsDictionary               ] = useState<Keyword[]>([]);
  const [ categoriesLoaded                 , setCategoriesLoaded                 ] = useState(false);
  const [ usedKeywords                     , setUsedKeywords                     ] = useState<Keyword[]>([]);
  const [ availableUserStatus              , setAvailableUserStatus              ] = useState<UserStatus[]>([]);
  const [ vodProducts                      , setVodProducts                      ] = useState<Product[]>([]);

  const [ id                               ] = useState(initialVideo.id);
  const [ uuid                             ] = useState(initialVideo.uuid);
  const [ creationDate                     ] = useState(initialVideo.creationDate);
  const [ creationIpAddress                ] = useState(initialVideo.creationIpAddress);
  const defaultCreationDate = isEditMode ? initialVideo.releaseDate : new Date();
  const [ title                            , setTitle                            ] = useState(prefillFromRecording ? prefillFromRecording.filename : initialVideo.title);
  const [ shortTitle                       , setShortTitle                       ] = useState(initialVideo.shortTitle);
  const [ complementToTheTitle             , setComplementToTheTitle             ] = useState(initialVideo.complementToTheTitle);
  const [ description                      , setDescription                      ] = useState(initialVideo.description);
  const [ duration                         , setDuration                         ] = useState(recordingDurationHms || initialVideo.duration);
  const [ distribution                     , setDistribution                     ] = useState(initialVideo.distribution);
  const [ production                       , setProduction                       ] = useState(initialVideo.production);
  const [ designer                         , setDesigner                         ] = useState(initialVideo.designer);
  const [ copyright                        , setCopyright                        ] = useState(initialVideo.copyright);
  const [ firstUseDate                     , setFirstUseDate                     ] = useState(initialVideo.firstUseDate);
  const [ releaseDate                      , setReleaseDate                      ] = useState(defaultCreationDate);
  const [ rankingStr                       ] = useState(`${initialVideo.ranking}`);
  const [ numberOfViewsStr                 ] = useState(`${initialVideo.numberOfViews}`);
  const [ categoriesIds                    , setCategoriesIds                    ] = useState(initialVideo.categoriesIds);
  const [ keywordsIds                      , setKeywordsIds                      ] = useState(initialVideo.keywordsIds);
  const [ youtubeId                        , setYoutubeId                        ] = useState(initialVideo.youtubeId);
  const [ requiredUserStatusId             , setRequiredUserStatusId             ] = useState(initialVideo.requiredUserStatusId);

  const [ commentsAllowed                  , setCommentsAllowed                  ] = useState(initialVideo.commentsModeration !== -1);
  const [ commentsModerationLevel          , setCommentsModerationLevel          ] = useState(initialVideo.commentsModeration >= 0 ? initialVideo.commentsModeration.toString() : DEFAULT_COMMENTS_LEVEL);

  const [ associatedProduct                , setAssociatedProduct                ] = useState<Product|null>(null);

  const [ soldProduct                      , setSoldProduct                      ] = useState (initialVideo.associatedProductIds.length > 0);
  const [                , setInitialSoldProduct               ] = useState (initialVideo.associatedProductIds.length > 0);
  const [ soldProductPrice                 , setSoldProductPrice                 ] = useState ('');
  const [ initialSoldProductPrice          , setInitialSoldProductPrice          ] = useState ('');

  const [ active                           , setActive                           ] = useState(initialVideo.active);
  // Question posée au clic sur « Créer » quand la vidéo n'est pas active.
  // `null` = pas encore posée ; la fenêtre reste ouverte jusqu'à Oui ou Non,
  // les deux poursuivent la création — il n'y a pas de troisième issue.
  const publishModeRef = useRef<'now' | 'scheduled' | null>(null);
  const [ activationQuestionToShow         , setActivationQuestionToShow         ] = useState(false);
  const [ activeScheduled                  , setActiveScheduled                  ] = useState( initialVideo.activeScheduled );
  const [ activeStartDate                  , setActiveStartDate                  ] = useState<Date|null>(initialVideo.activeStartDate);
  const [ activeEndDate                    , setActiveEndDate                    ] = useState<Date|null>(initialVideo.activeEndDate);
  const [ publicPortal                     , setPublicPortal                     ] = useState(initialVideo.publicPortal)
  const [ automaticPlaylist                , setAutomaticPlaylist                ] = useState(initialVideo.automaticPlaylist)

  const [ connectedSocialNetworks          , setConnectedSocialNetworks          ] = useState<AdminSocialNetworkStatus[]>([]);
  const [ socialNetworkPublications        , setSocialNetworkPublications        ] = useState<SocialNetworkPublicationStatus[]>([]);
  // Visibilité demandée à la publication — "private" par défaut : Kleon ne
  // décide pas de rendre public un compte qui n'est pas le sien sans que le
  // créateur l'ait choisi explicitement.
  const [ socialNetworkPrivacyStatusByCode , setSocialNetworkPrivacyStatusByCode ] = useState<Record<string, SocialNetworkPrivacyStatus>>({});
  // En création : la vidéo n'existe pas encore, donc pas de publication possible
  // tout de suite — juste une intention, à usage unique, transmise à createVideo()
  // et consommée par l'API une fois SON transcodage terminé (même principe
  // qu'activateAfterTranscoding). Sans objet en édition, où le switch publie
  // immédiatement via handleSocialNetworkToggle.
  const [ socialNetworksToPublishAtCreation, setSocialNetworksToPublishAtCreation ] = useState<Record<string, boolean>>({});

  // Bulle « réseau non configuré » : posée au clic sur le switch grisé, effacée toute seule.
  // Position `fixed` calculée sur le switch et portée sur document.body, comme la bulle de
  // renommage de RecordingStorageModal : aucun conteneur de la fiche ne peut la rogner.
  const [ notConfiguredBubble , setNotConfiguredBubble ] = useState<{ top: number; left: number } | null>(null);
  const notConfiguredBubbleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (notConfiguredBubbleTimeoutRef.current) clearTimeout(notConfiguredBubbleTimeoutRef.current); }, []);

  // Bulle « sera publiée après la création » : posée au survol du switch coché
  // en création (rien d'autre à montrer, la vidéo n'existe pas encore — voir
  // socialNetworksToPublishAtCreation). Même portail que la bulle ci-dessus,
  // mais suit la souris (entrée/sortie) au lieu de s'effacer toute seule.
  const [ pendingPublishBubble , setPendingPublishBubble ] = useState<{ top: number; left: number } | null>(null);

  const [ imageUrl                         , setImageUrl                         ] = useState( (initialVideo.publicRef && initialVideo.publicRef !== '') ? `${WWW_URLS.MediaServer}${initialVideo.publicRef}/images/thumbnail_BIG.webp` : '' );
  const [ imageFileToUpload                , setImageFileToUpload                ] = useState<File | null>(null);
  const [ imageSrc                         , setImageSrc                         ] = useState( (initialVideo.publicRef && initialVideo.publicRef !== '') ? `${WWW_URLS.MediaServer}${initialVideo.publicRef}/images/thumbnail_BIG.webp` : '' );

  const [ videoFile                        ] = useState( (initialVideo.publicRef && initialVideo.publicRef !== '') ? `${WWW_URLS.MediaServer}${initialVideo.publicRef}/videos/001/index.m3u8` : '' );
  const [ videoFileToUpload                , setVideoFileToUpload                ] = useState<File | null>(null);

  // Le sélecteur de fichier vit DANS le FieldMedia de chaque aperçu : c'est lui qui sait
  // lire le fichier, en tirer la durée et rafraîchir l'aperçu. On lui prête ces
  // références pour pouvoir l'ouvrir depuis le bouton de la ligne de titre, au lieu de
  // dupliquer un second <input type="file"> qui, lui, ne préviendrait personne.
  const videoFileInputRef = useRef<HTMLInputElement>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);
  // Qualités choisies dans la fenêtre précédente, en attente de la réponse à la
  // question d'activation. Une ref et pas un état : elles ne s'affichent nulle
  // part, et un rendu de plus n'apporterait rien.
  const pendingResolutionsRef = useRef<HlsResolutionId[] | undefined>(undefined);
  // Réglage de compression choisi dans la fenêtre des qualités. Une ref en plus de l'état : save()
  // est rappelé aussitôt après le choix (voir handleVideoQualitySelectionConfirm), avant que l'état
  // ne soit à jour — même raison que pour les qualités.
  const compressionRef        = useRef<CompressionLevel>(DEFAULT_COMPRESSION_LEVEL);
  const [ videoSrc                         ] = useState( (initialVideo.publicRef && initialVideo.publicRef !== '') ? `${WWW_URLS.MediaServer}${initialVideo.publicRef}/videos/001/index.m3u8` : '' );

  // Remontage forcé d'un aperçu. `FieldMedia` bascule sur son propre aperçu en blob
  // dès qu'un fichier est choisi et ne le lâche plus quand le parent vide `value` —
  // l'effet qui remet son état à zéro est gardé par `!isBlob`. Changer sa clé le refait
  // à neuf, et son démontage révoque l'objectURL au passage.
  const [ imagePreviewKey                  , setImagePreviewKey                  ] = useState(0);
  const [ videoPreviewKey                  , setVideoPreviewKey                  ] = useState(0);

  // Vignette de la fiche effacée à la main. À l'enregistrement, on ne va pas en
  // extraire une nouvelle de la vidéo : ce serait défaire le geste. C'est le logo
  // qui prend la place.
  const [ imageDeletionRequested           , setImageDeletionRequested           ] = useState(false);

  const [ titleErrorMessage                , setTitleErrorMessage                ] = useState('');
  const [ descriptionErrorMessage          , setDescriptionErrorMessage          ] = useState('');
  const [          , setFirstUseDateErrorMessage         ] = useState('');
  const [           , setReleaseDateErrorMessage          ] = useState('');
  const [ imageFileToUploadErrorMessage    , setImageFileToUploadErrorMessage    ] = useState('');
  const [ videoFileToUploadErrorMessage    , setVideoFileToUploadErrorMessage    ] = useState('');
  const [ youtubeIdErrorMessage            ] = useState('');
  const [ categoriesIdsErrorMessage        , setCategoriesIdsErrorMessage        ] = useState('');
  const [ requiredUserStatusIdErrorMessage , setRequiredUserStatusIdErrorMessage ] = useState('');


  // Le nom du fichier tel que l'utilisateur l'a téléversé, affiché à côté du titre de
  // chaque aperçu. Un fichier fraîchement choisi prime sur celui de la fiche : c'est
  // lui qui partira à l'enregistrement, c'est donc lui qu'il faut lire.
  const originalImageName = imageFileToUpload?.name ?? initialVideo.originalImageFile ?? '';
  const originalVideoName = videoFileToUpload?.name ?? initialVideo.originalFile      ?? '';

  const [ numberOfModifiedFields           , setNumberOfModifiedFields           ] = useState(0);
  const [ saved                            , setSaved                            ] = useState(false);
  const [ cancelation                      , setCancelation                      ] = useState(false);
  const [ error                            , setError                            ] = useState(false);
  const [                          , setErrorMsg                         ] = useState('');
  const [ progress                         , setProgress                         ] = useState(0);
  const [ saveButtonLock                   , setSaveButtonLock                   ] = useState(false);

  const [ categoryCreationWindowToShow     , setCategoryCreationWindowToShow     ] = useState ( false );

  const [ videoQualityWindowToShow         , setVideoQualityWindowToShow         ] = useState ( false );
  const [ selectedResolutions              , setSelectedResolutions              ] = useState<HlsResolutionId[]> ( [] );
  const [ sourceIs4K                       , setSourceIs4K                       ] = useState<boolean>           ( false );

  // Taille du fichier choisi, lue par le navigateur. S'il ne sait pas le décoder (HEVC, souvent),
  // la taille reste inconnue et le 2160p n'est pas proposé : on ne prétend pas à une 4K non vérifiée.
  useEffect(() => {
    setSourceIs4K(false);
    if (!videoFileToUpload) return;
    const objectUrl = URL.createObjectURL(videoFileToUpload);
    const probe     = document.createElement('video');
    probe.preload   = 'metadata';
    probe.muted     = true;
    probe.onloadedmetadata = () => setSourceIs4K(isSource4K(probe.videoWidth, probe.videoHeight));
    probe.src = objectUrl;
    return () => {
      probe.onloadedmetadata = null;
      probe.removeAttribute('src');
      probe.load();
      URL.revokeObjectURL(objectUrl);
    };
  }, [videoFileToUpload]);
  const [ selectedCompression              , setSelectedCompression              ] = useState<CompressionLevel>  ( DEFAULT_COMPRESSION_LEVEL );

  const [ documents                        , setDocuments                        ] = useState<DocumentWithState[]> ( [] );

  const [ errorYoutubeRef                  , setErrorYoutubeRef                  ] = useState (false)

  const [ showWaitingIcon        , setShowWaitingIcon        ] = useState(false);

  let videoSheetId        : number = 0
  let associatedProductId : number = 0

  useEffect(() => {

    if (!isEditMode || !video) { setDocuments([]); }

    if (isEditMode) { setShowWaitingIcon(true); }

    const fetchData = async () => {
      try
       {
        const [ categoriesResult          ,
                keywordsResult            ,
                availableUserStatusResult ,
                vodProductsResult         ,
                documentsResult           ] = await Promise.all ([ getMediaCategories(ownerAdminId || authentication.admin?.id) ,
                                                                   getKeywords()           ,
                                                                   getUserStatusList()     ,
                                                                   getProducts("Boutique", ownerAdminId || authentication.admin?.id) ,
                                                                   isEditMode && video ? getDocumentsByEntityId('video', video.id) : Promise.resolve(null) ]);

        if (categoriesResult.code === 1)
         {
          setVideoCategories(categoriesResult.payload.sort((a, b) => a.rank - b.rank));
          setCategoriesLoaded(true);
         }

        if (documentsResult && documentsResult.code === 1)
         {
          setDocuments(
            documentsResult.payload.map(doc => ({
              ...doc,
              isNew: false,
              isMarkedForDeletion: false,
            }))
          );
         }

        if (keywordsResult.code === 1)
         {
          setKeywordsDictionary(keywordsResult.payload);

          if (video && keywordsResult.payload.length > 0)
           {
            const used = keywordsResult.payload.filter(keyword => video.keywordsIds.includes(keyword.id));
            setUsedKeywords(used);
           }
         }

        if (availableUserStatusResult.code === 1)
         {
          setAvailableUserStatus (availableUserStatusResult.payload);
         }

        if (vodProductsResult.code === 1)
         {
          setVodProducts (vodProductsResult.payload);
         }
       }
      catch (error)
       {
        console.error('Error fetching data:', error);
       }
      finally
       {
        if (isEditMode) { setShowWaitingIcon(false); }
       }
    };

    fetchData();

  }, [video]);

  useEffect(() => {
    if (video && keywordsDictionary.length > 0) {
      const used = keywordsDictionary.filter(keyword => video.keywordsIds.includes(keyword.id));
      setUsedKeywords(used);
    }
  }, [video, keywordsDictionary]);

  useEffect(() => {
    // En création, le select affiche le premier statut ("Accès gratuit") dès que la liste
    // arrive, mais requiredUserStatusId reste à 0 tant que l'utilisateur ne le touche pas :
    // on aligne l'état sur ce qui est visuellement présélectionné pour éviter une erreur
    // de validation sur un champ que l'utilisateur croit déjà renseigné.
    if (!isEditMode && requiredUserStatusId === 0 && availableUserStatus.length > 0) {
      setRequiredUserStatusId(availableUserStatus[0].id);
    }
  }, [isEditMode, requiredUserStatusId, availableUserStatus]);

  // Réseaux sociaux connectés par ce créateur — indépendant de fetchData
  // ci-dessus : ce n'est pas une donnée de LA vidéo, mais du compte, comme
  // dans AdminSheet.
  const reloadConnectedSocialNetworks = () => {
    const adminId = authentication.admin?.id;
    if (!adminId) { return; }

    listAdminSocialNetworks(adminId)
      .then(setConnectedSocialNetworks)
      .catch(() => { /* les switches restent verrouillés plutôt que de bloquer la fiche */ });
  };

  useEffect(() => {
    if (!authentication.admin?.id) { return; }

    reloadConnectedSocialNetworks();

    // La connexion d'un réseau s'ouvre dans un nouvel onglet (cf. handleConnectSocialNetwork) :
    // on recharge au retour de focus, comme la section réseaux de la fiche Créateur, pour que
    // le switch se débloque sans rouvrir la fiche.
    window.addEventListener('focus', reloadConnectedSocialNetworks);
    return () => window.removeEventListener('focus', reloadConnectedSocialNetworks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication.admin?.id]);

  // Historique de publication de CETTE vidéo — seulement en édition, une
  // vidéo pas encore enregistrée n'a pas d'id à publier. Poll en continu tant
  // que la fiche reste ouverte : la commande de publication tourne en tâche
  // de fond côté admin-api, rien ne pousse l'état ici (même limite que le
  // polling déjà en place ailleurs sur AbstractMedia::state pour le
  // transcodage) — pas de logique d'arrêt anticipé, le coût d'un appel toutes
  // les 5s est négligeable face à la complexité d'un arrêt correct.
  useEffect(() => {
    if (!isEditMode || !id) { return; }

    let cancelled = false;

    const reload = () => {
      listVideoSocialNetworkPublications(id)
        .then(list => { if (!cancelled) { setSocialNetworkPublications(list); } })
        .catch(() => { /* laisse l'état précédent plutôt que de l'effacer */ });
    };

    reload();

    const intervalId = setInterval(reload, 5000);

    return () => { cancelled = true; clearInterval(intervalId); };
  }, [isEditMode, id]);

  const latestPublicationFor = (code: string): SocialNetworkPublicationStatus | undefined =>
    socialNetworkPublications.find(p => p.socialNetworkCode === code);

  const handleSocialNetworkPrivacyStatusChange = (code: string, value: SocialNetworkPrivacyStatus) => {
    setSocialNetworkPrivacyStatusByCode(prev => ({ ...prev, [code]: value }));
  };

  // Réseau dont on demande la déconnexion : retenu le temps de la question.
  const [ socialNetworkToDisconnect , setSocialNetworkToDisconnect ] = useState<AdminSocialNetworkStatus | null>(null);

  const handleDisconnectSocialNetwork = () => {
    const network = socialNetworkToDisconnect;
    setSocialNetworkToDisconnect(null);
    if (!network || !authentication.admin?.id || network.adminSocialNetworkId == null) { return; }

    disconnectSocialNetwork(authentication.admin.id, network.adminSocialNetworkId)
      .then(reloadConnectedSocialNetworks)
      .catch(() => { /* pas de fenêtre d'erreur dédiée : un nouvel essai suffit */ });
  };

  // Même chemin que le bouton « Connecter » de la section réseaux de la fiche Créateur.
  const handleConnectSocialNetwork = (code: string) => {
    if (!authentication.admin?.id) { return; }

    getSocialNetworkAuthorizeUrl(authentication.admin.id, code)
      .then(url => { window.open(url, '_blank', 'noopener,noreferrer'); })
      .catch(() => { /* pas de fenêtre d'erreur dédiée : un nouvel essai suffit */ });
  };

  const showNotConfiguredBubble = (anchor: HTMLElement) => {
    const rect = anchor.getBoundingClientRect();
    setNotConfiguredBubble({ top: rect.top - 6, left: rect.left });
    if (notConfiguredBubbleTimeoutRef.current) clearTimeout(notConfiguredBubbleTimeoutRef.current);
    notConfiguredBubbleTimeoutRef.current = setTimeout(() => setNotConfiguredBubble(null), 3000);
  };

  const showPendingPublishBubble = (anchor: HTMLElement) => {
    const rect = anchor.getBoundingClientRect();
    setPendingPublishBubble({ top: rect.top - 6, left: rect.left });
  };

  const handleSocialNetworkToggle = (network: AdminSocialNetworkStatus, newState: boolean) => {
    if (!network.connected || !network.implemented) { return; }

    // Pas encore d'id : la vidéo n'existe pas, donc rien à publier tout de
    // suite — juste une intention, réversible, envoyée à la création (voir
    // socialNetworksToPublishAtCreation et handleSaveButton).
    if (!id) {
      setSocialNetworksToPublishAtCreation(prev => ({ ...prev, [network.code]: newState }));
      return;
    }

    if (!newState || !initialVideo.transcoded) { return; }

    const current = latestPublicationFor(network.code);
    if (current && SOCIAL_NETWORK_LOCKED_STATUSES.includes(current.status)) { return; }

    const privacyStatus = socialNetworkPrivacyStatusByCode[network.code] ?? 'private';

    publishVideoToSocialNetwork(id, network.code, privacyStatus)
      .then(publication => { setSocialNetworkPublications(prev => [publication, ...prev]); })
      .catch(() => { /* pas de fenêtre d'erreur dédiée : le statut Error du prochain polling suffit */ });
  };

  useEffect(() => {

    if ( video &&
         video.associatedProductIds &&
         video.associatedProductIds.length > 0 )
     {
      const foundProd = vodProducts.find ( (p) => p.id === video.associatedProductIds[0] );

      if (foundProd)
	   {
        setAssociatedProduct(foundProd);
       }
     }
  }, [vodProducts, video]);

  useEffect(() => {

    if ( associatedProduct !== null )
     {
      if ( video )
	   {
        setSoldProduct        (video.associatedProductIds.length > 0)
        setInitialSoldProduct (video.associatedProductIds.length > 0)
       }

      if ( associatedProduct.variants )
       {
          if ( associatedProduct.variants[0] )
         {
          if ( associatedProduct.variants[0].prices )
           {
              if ( associatedProduct.variants[0].prices[0] )
             {
              setSoldProductPrice        ( ( associatedProduct.variants[0].prices[0].priceWithTax).toFixed(2) )
              setInitialSoldProductPrice ( ( associatedProduct.variants[0].prices[0].priceWithTax).toFixed(2) )
             }
           }
         }
       }
     }

  }, [associatedProduct]);


  function extractYouTubeVideoId (input: string): string | null
   {
    try
     {
      const idPattern = /^[a-zA-Z0-9_-]{11}$/;

      if (idPattern.test(input))
       {
        return input;
       }

      const parsedUrl = new URL(input);
      const hostname  = parsedUrl.hostname;

      if (hostname.includes('youtube.com') && parsedUrl.pathname === '/watch')
       {
        return parsedUrl.searchParams.get('v');
       }

      if (hostname === 'youtu.be')
       {
        return parsedUrl.pathname.split('/')[1];
       }

      if (hostname.includes('youtube.com') && parsedUrl.pathname.startsWith('/embed/'))
       {
        return parsedUrl.pathname.split('/')[2];
       }

      const match = parsedUrl.pathname.match(/\/(v|embed|shorts)\/([a-zA-Z0-9_-]{11})/);

      if (match && match[2])
       {
        return match[2];
       }

      return null;
     }
    catch
     {
      return null;
     }
  }

  // Comme Youtube : si aucune vignette n'est fournie, on en extrait une depuis la vidéo.
  const extractThumbnailFromVideo = (source: File | string): Promise<File | null> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';
      video.muted = true;

      const objectUrl = source instanceof File ? URL.createObjectURL(source) : null;
      video.src = source instanceof File ? objectUrl! : source;

      const finish = (result: File | null) => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        resolve(result);
      };

      const timeout = setTimeout(() => finish(null), 8000);

      video.onloadedmetadata = () => {
        video.currentTime = Math.min(1, (video.duration || 0) / 2);
      };

      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          if (canvas.width === 0 || canvas.height === 0) { clearTimeout(timeout); finish(null); return; }

          const ctx = canvas.getContext('2d');
          if (!ctx) { clearTimeout(timeout); finish(null); return; }

          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => {
            clearTimeout(timeout);
            finish(blob ? new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' }) : null);
          }, 'image/jpeg', 0.85);
        } catch {
          clearTimeout(timeout);
          finish(null);
        }
      };

      video.onerror = () => { clearTimeout(timeout); finish(null); };
    });
  };

  // Vignette de remplacement : le logo Kleon, à plat sur fond noir.
  //
  // Le logo anime ses dégradés et son anneau par des balises SMIL (`animateTransform`).
  // Dessiné tel quel sur un canvas, il donnerait une vignette différente à chaque
  // enregistrement, selon l'instant du dessin : on retire donc les animations du
  // document avant d'en faire une image. Le SVG ne porte qu'un `viewBox` — sans
  // `width`/`height` explicites, certains navigateurs le chargent avec une taille
  // naturelle nulle et le canvas ressort vide.
  const getPlaceholderThumbnailFile = async (): Promise<File | null> => {
    let objectUrl: string | null = null;

    try {
      const response = await fetch('/images/logo.svg');
      const markup   = await response.text();

      const document_ = new DOMParser().parseFromString(markup, 'image/svg+xml');
      document_.querySelectorAll('animate, animateTransform, animateMotion, set').forEach(node => node.remove());
      document_.documentElement.setAttribute('width' , '600');
      document_.documentElement.setAttribute('height', '600');

      const svg = new XMLSerializer().serializeToString(document_.documentElement);
      objectUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));

      const logo = await new Promise<HTMLImageElement | null>((resolve) => {
        const image   = new Image();
        image.onload  = () => resolve(image);
        image.onerror = () => resolve(null);
        image.src     = objectUrl!;
      });

      if (!logo) { return null; }

      // Mêmes dimensions que le thumbnail_BIG produit par l'API.
      const canvas  = document.createElement('canvas');
      canvas.width  = 1920;
      canvas.height = 1080;

      const context = canvas.getContext('2d');
      if (!context) { return null; }

      context.fillStyle = '#000000';
      context.fillRect(0, 0, canvas.width, canvas.height);

      // Le logo occupe toute la hauteur : son viewBox lui garde de la marge (l'anneau
      // et ses nœuds n'en remplissent qu'environ 85 %), la vignette n'est donc pas
      // rognée au bord.
      const size = canvas.height;
      context.drawImage(logo, (canvas.width - size) / 2, (canvas.height - size) / 2, size, size);

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));

      return blob ? new File([blob], 'logo-kleon.png', { type: 'image/png' }) : null;
    } catch {
      return null;
    } finally {
      if (objectUrl) { URL.revokeObjectURL(objectUrl); }
    }
  };

  const retrieveInformationsFromYoutube = async () => {

    const extractedYoutubeId = extractYouTubeVideoId (youtubeIdForRetrieving)

    if (!extractedYoutubeId)
          {
           setErrorYoutubeRef (true)
          }
	 else {
           const informations = await getInformationsFromYoutube(extractedYoutubeId!);

           if (informations.code === 1)
            {
             setTitle(informations.title);
             setShortTitle(informations.title);
             setComplementToTheTitle('');
             setDescription(informations.description);
             setDuration(formatDurationFromIso(informations.duration));
             setDistribution('');
             setProduction('');
             setCopyright('');
             setImageSrc(informations.thumbnailUrl);
             setImageUrl(informations.thumbnailUrl);
             setImageFileToUpload(null);

             const d1 = convertStringToDate(informations.publishedAt);
             const d2 = convertDateToString(d1, 'dd/MM/yyyy HH:mm:ss', 'Europe/Paris');
             const d3 = convertStringToDate(d2);

             setReleaseDate(d3);
             setFirstUseDate(d3);
             setYoutubeId(extractedYoutubeId!);

             const titleErrorMessageBis = testTitle(informations.title);
             const releaseDateErrorMessageBis = testReleaseDate(convertDateToString(d3, 'dd/MM/yyyy'));
             const firstUseDateErrorMessageBis = testFirstUseDate(convertDateToString(d3, 'dd/MM/yyyy'));

             setNumberOfModifiedFields(14);
             setTitleErrorMessage(titleErrorMessageBis);
             setReleaseDateErrorMessage(releaseDateErrorMessageBis);
             setFirstUseDateErrorMessage(firstUseDateErrorMessageBis);
             setImageFileToUploadErrorMessage('');
            }
          }
  };

const save = async (resolutionsOverride?: HlsResolutionId[], activateAfterTranscoding: boolean = false) => {

  const resolutionsToUse = resolutionsOverride ?? selectedResolutions;

  const titleErrorMessageBis                = testTitle                (title);
  const releaseDateErrorMessageBis          = testReleaseDate          (convertDateToString(releaseDate, 'dd/MM/yyyy'));
  const paidPriceValue                      = parseFloat(soldProductPrice);
  const requiredUserStatusIdErrorMessageBis = requiredUserStatusId === PAID_STATUS_ID && !(paidPriceValue > 0)
                                              ? 'Indiquez un prix TTC supérieur à 0 pour un accès payant.'
                                              : testRequiredUserStatusId (requiredUserStatusId)
  const categoriesIdsErrorMessageBis        = testCategoriesIds        (categoriesIds)

  // Un enregistrement désigné vaut un fichier choisi : la source est garantie
  // avant même l'ouverture de la fiche, testVideoFileToUpload n'a rien à valider.
  const videoFileToUploadErrorMessageBis = prefillFromRecording
    ? ''
    : testVideoFileToUpload (videoFileToUpload === null ? undefined : videoFileToUpload);
  testVideoUrl          (videoFile);

  // En édition, une catégorie, un statut requis ou une vidéo manquants dans une fiche
  // existante ne bloquent pas l'enregistrement d'une autre modification : on ne les
  // exige que si l'opérateur a touché à ce champ (2026-10-22).
  const sameIds = (a: number[], b: number[]) => JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
  const categoriesIdsTouched        = !isEditMode || !sameIds(categoriesIds, initialVideo.categoriesIds);
  const requiredUserStatusIdTouched = !isEditMode || requiredUserStatusId !== initialVideo.requiredUserStatusId;
  const videoFileTouched            = !isEditMode || videoFileToUpload !== null;

  const categoriesIdsBlocking        = categoriesIdsTouched        ? categoriesIdsErrorMessageBis        : '';
  const requiredUserStatusIdBlocking = requiredUserStatusIdTouched ? requiredUserStatusIdErrorMessageBis : '';
  const videoFileBlocking            = videoFileTouched            ? videoFileToUploadErrorMessageBis    : '';

  setTitleErrorMessage                (titleErrorMessageBis);
  setDescriptionErrorMessage          ('');
  setReleaseDateErrorMessage          (releaseDateErrorMessageBis);
  setCategoriesIdsErrorMessage        (categoriesIdsBlocking);
  setRequiredUserStatusIdErrorMessage (requiredUserStatusIdBlocking);
  setImageFileToUploadErrorMessage    ('');
  setVideoFileToUploadErrorMessage    (videoSrc === '' ? videoFileBlocking : '');

  const test = titleErrorMessageBis                === "" &&
               releaseDateErrorMessageBis          === "" &&
               requiredUserStatusIdBlocking        === "" &&
               categoriesIdsBlocking               === "" &&
               (videoSrc === '' && videoFileBlocking === "" || videoSrc !== '');

  if (numberOfModifiedFields > 0 && test)
   {
    setShowWaitingIcon(true);

    // La vignette n'est pas obligatoire : à défaut, on en extrait une de la vidéo
    // (comme Youtube), sinon on utilise une image de remplacement.
    let imageUrlToSave           = imageUrl;
    let imageFileToUploadToSave  = imageFileToUpload;

    if (imageSrc === '' && imageFileToUploadToSave === null)
     {
      const videoSourceForThumbnail = imageDeletionRequested
                                      ? null
                                      : (videoFileToUpload ?? (videoSrc !== '' ? videoSrc : null));
      const extractedThumbnail      = videoSourceForThumbnail ? await extractThumbnailFromVideo(videoSourceForThumbnail) : null;
      const thumbnailFile           = extractedThumbnail ?? await getPlaceholderThumbnailFile();

      if (thumbnailFile)
       {
        imageFileToUploadToSave = thumbnailFile;
        setImageFileToUpload (thumbnailFile);
        setImageSrc          (URL.createObjectURL(thumbnailFile));
       }
     }

    // Le statut sélectionné fait foi : `soldProduct` ne reflète que l'état à l'ouverture de la fiche.
    if (requiredUserStatusId === PAID_STATUS_ID)
          {
           const soldProductPriceValue = parseFloat(soldProductPrice);

           let associatedProduct_price   : ProductPrice;
           let associatedProduct_variant : ProductVariant;
           let associatedProduct2        : Product;

           if (isEditMode && associatedProduct)
                 {
                  const variant = associatedProduct.variants[0];
                  const price = variant.prices[0];

                  associatedProduct_price = {
                                              id             : price.id,
                                              name           : price.name,
                                              requiredStatus : price.requiredStatus,
                                              comment        : price.comment,
                                              code           : price.code,
                                              active         : price.active,
                                              creationDate   : price.creationDate,
                                              basePrice      : soldProductPriceValue / 1.2,
                                              taxRate        : 20,
                                              priceWithTax   : soldProductPriceValue,
                                              discounts      : [],
                                            };

                  associatedProduct_variant = {
          id: variant.id,
          name: variant.name,
          complement: variant.complement,
          description: variant.description,
          code: variant.code,
          barcode: variant.barcode,
          active: variant.active,
          creationDate: variant.creationDate,
          stockAvailable: variant.stockAvailable,
          stockAssigned: variant.stockAssigned,
          stockBooked: variant.stockBooked,
          stockThreshold: variant.stockThreshold,
          characteristics: variant.characteristics,
          medias: variant.medias,
          prices: [associatedProduct_price],
        };

        associatedProduct2 = {
          id: associatedProduct.id,
          uuid: associatedProduct.uuid,
          publicRef: associatedProduct.publicRef,
          name: associatedProduct.name,
          catchword: associatedProduct.catchword,
          code: associatedProduct.code,
          publicPortal: associatedProduct.publicPortal,
          active: associatedProduct.active,
          activeScheduled: associatedProduct.activeScheduled,
          activeStartDate: associatedProduct.activeStartDate,
          activeEndDate: associatedProduct.activeEndDate,
          lastUpdateDate: associatedProduct.lastUpdateDate,
          creationDate: associatedProduct.creationDate,
          requiredStatus: associatedProduct.requiredStatus,
          requiredUserStatusId: associatedProduct.requiredUserStatusId,
          paymentRequired: associatedProduct.paymentRequired,
          kind: associatedProduct.kind,
          description: associatedProduct.description,
          variants: [associatedProduct_variant],
          characteristics: associatedProduct.characteristics,
          medias: associatedProduct.medias,
          categoriesIds: associatedProduct.categoriesIds,
          associatedMediaId: associatedProduct.associatedMediaId,
          associatedBroadcastingChannelId: 0,
          associatedUserStatusId: 0,
          associatedAdminResource: null
        };

        await updateProduct(associatedProduct2, null, ownerAdminId || authentication.admin?.id);
        // Un produit masqué par un passage au gratuit doit redevenir visible au retour au payant.
        await updateProductVisibility(associatedProduct.id, true);
        await refreshProductById(associatedProduct.id);
        associatedProductId = associatedProduct.id;
      } else {
        associatedProduct_price = {
          id: 0,
          name: title,
          requiredStatus: null,
          comment: null,
          code: 'p1',
          active: true,
          creationDate: new Date(),
          basePrice: soldProductPriceValue / 1.2,
          taxRate: 20,
          priceWithTax: soldProductPriceValue,
          discounts: [],
        };

        associatedProduct_variant = {
          id                              : 0,
          name                            : title,
          complement                      : '',
          description                     : '',
          code                            : 'v0',
          barcode                         : '',
          active                          : true,
          creationDate                    : new Date(),
          stockAvailable                  : 0,
          stockBooked                     : 0,
          stockAssigned                   : 0,
          stockThreshold                  : 0,
          characteristics                 : [],
          medias                          : [],
          prices                          : [associatedProduct_price],
        };

        associatedProduct2 = {
          id                              : 0,
          uuid                            : '',
          publicRef                       : '',
          name                            : title,
          catchword                       : '',
          code                            : '',
          publicPortal                    : publicPortal,
          active                          : true,
          activeScheduled                 : activeScheduled,
          activeStartDate                 : activeStartDate,
          activeEndDate                   : activeEndDate,
          creationDate                    : new Date(),
          lastUpdateDate                  : null,
          requiredStatus                  : requiredUserStatusId > 0 ? availableUserStatus[requiredUserStatusId-1].name : '',
          requiredUserStatusId            : requiredUserStatusId,
          paymentRequired                 : true,
          kind                            : 'PayVideo',
          description                     : description,
          variants                        : [associatedProduct_variant],
          characteristics                 : [],
          medias                          : [],
          categoriesIds                   : [1],
          associatedMediaId               : 0,
          associatedBroadcastingChannelId : 0,
          associatedUserStatusId          : 0,
          associatedAdminResource         : null
        };

        const result2 = await createProduct(associatedProduct2, null, ownerAdminId || authentication.admin?.id);

        if (result2.code === 1) {
          associatedProductId = result2.payload;
          await refreshProductById(associatedProductId);
        }
      }
    } else {
      if (isEditMode && associatedProduct) {
        await updateProductVisibility(associatedProduct.id, false);
        await refreshProductById(associatedProduct.id);
      }
    }

    try {
      setSaveButtonLock(true);

      if (uploadProgressFunction) uploadProgressFunction(0);
      setSaved(false);
      setError(false);

      const commentsModerationToSave = !commentsAllowed ? -1 : parseInt(commentsModerationLevel, 10);

      const result = isEditMode ?
        await updateVideo(
          id, title, shortTitle, complementToTheTitle, description, duration,
          distribution, production, copyright, releaseDate, firstUseDate,
          rankingStr, numberOfViewsStr, imageUrlToSave, imageFileToUploadToSave,
          videoFileToUpload, youtubeId, requiredUserStatusId,  associatedProductId, categoriesIds,
          keywordsIds, publicPortal, commentsModerationToSave, active, activeScheduled, activeStartDate, activeEndDate, documents,
          uploadProgressFunction, authentication.admin?.id, designer, resolutionsToUse, automaticPlaylist, compressionRef.current
        ) :
        await createVideo(
          title, shortTitle, complementToTheTitle, description, duration,
          distribution, production, copyright, releaseDate, firstUseDate,
          rankingStr, numberOfViewsStr, imageUrlToSave, imageFileToUploadToSave,
          videoFileToUpload, youtubeId, requiredUserStatusId, associatedProductId, categoriesIds,
          keywordsIds, publicPortal, commentsModerationToSave, active, activeScheduled, activeStartDate, activeEndDate, documents,
          uploadProgressFunction, authentication.admin?.id, designer, resolutionsToUse, activateAfterTranscoding,
          prefillFromRecording
            ? { channelUuid: prefillFromRecording.channelUuid, sessionId: prefillFromRecording.sessionId,
                publishAngles: (prefillFromRecording.angleCount ?? 0) > 0 && publishAngles }
            : null,
          Object.entries(socialNetworksToPublishAtCreation)
            .filter(([, wanted]) => wanted)
            .map(([code]) => ({ code, privacyStatus: socialNetworkPrivacyStatusByCode[code] ?? 'private' })),
          automaticPlaylist,
          compressionRef.current
        );

      if (result.code === 1) {
        videoSheetId = isEditMode ? id : result.payload;

        const documentsToUpload: DocumentWithState[] = documents.filter(doc => doc.isNew && doc.fileToUpload);
        const documentsToDelete: number[] = documents
          .filter(doc => doc.isMarkedForDeletion && doc.id && doc.id > 0)
          .map(doc => doc.id!);

        if (documentsToDelete.length > 0) {
          await deleteDocuments(documentsToDelete);
        }

        for (const doc of documentsToUpload) {
          if (doc.fileToUpload) {
            await createDocument(
              doc.fileToUpload,
              doc.name,
              'video',
              videoSheetId,
              null,
              authentication.admin?.id,
              doc.isPublic
            );
          }
        }

        setSaved(true);
        setError(false);
        setProgress(0);
        setNumberOfModifiedFields(0);
        setSaveButtonLock(false);
        setShowWaitingIcon(false);

      } else {
        setSaved(true);
        setProgress(0);
        setError(true);
        setErrorMsg(result.comment);
        setSaveButtonLock(false);
        setShowWaitingIcon(false);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setSaved(true);
      setProgress(0);
      setError(true);
      setErrorMsg('Une erreur est survenue lors de la sauvegarde');
      setSaveButtonLock(false);
      setShowWaitingIcon(false);
    }
  } else {
    setSaveButtonLock(false);
  }
};

// Valeur inutilisée, mais l'abonnement est conservé : le retirer changerait
// la fréquence de rendu du composant.
useTypedSelector(getActiveUploads);
const videoUploads = useTypedSelector(state =>
  // Filtré sur le type : une image de même id remonterait sinon ici.
  getUploadsByEntityId(state, videoSheetId).filter(upload => upload.entityType === 'video')
);

useEffect(() => {
  if (videoUploads.length > 0) {
    const currentUpload = videoUploads[0];
    // Un upload terminé reste dans le store : rouvrir la fiche ne doit pas rallumer
    // la fenêtre de progression (sans bouton, elle bloquait la fiche à 90 %).
    if ([UploadStatus.COMPLETED, UploadStatus.CANCELLED, UploadStatus.FAILED].includes(currentUpload.status)) {
      setProgress(0);
    } else if (currentUpload.progress > 0) {
      setProgress(currentUpload.progress);
    }
  }
}, [videoUploads]);

  const handleCategoryAddingClick = () => {

    setCategoryCreationWindowToShow (true)

  }

  const handleCategoryCreationWindowClose = () => {

    const categoriesUpdate = async () => {

      const categoriesResult = await getMediaCategories(ownerAdminId || authentication.admin?.id)

      if (categoriesResult.code === 1)
       {
        setVideoCategories (categoriesResult.payload.sort((a, b) => a.rank - b.rank));
       }
     }

    categoriesUpdate ()

    setCategoryCreationWindowToShow (false)
  }

  const handleCategoryCreated = (createdCategory: VideoCategory) => {
    const categoriesUpdate = async () => {
      const categoriesResult = await getMediaCategories(ownerAdminId || authentication.admin?.id)
      if (categoriesResult.code === 1)
       {
        setVideoCategories (categoriesResult.payload.sort((a, b) => a.rank - b.rank));
       }
     }
    categoriesUpdate ()

    handleChoosenCategoryChange (true, createdCategory.id)
    setCategoryCreationWindowToShow (false)
  }


  const renderCategories = (): JSX.Element => {

    let title: string = "Aucune catégorie";
    let nbChoosenCategories: number = 0;

    // Case et nom poussés côte à côte, dans l'ordre de la grille (20px auto) :
    // avec grid-auto-flow en ligne, chaque paire tombe sur la MÊME ligne CSS,
    // qui s'ajuste à la hauteur du nom si celui-ci passe sur deux lignes.
    // Deux colonnes empilées séparément (l'ancien découpage) dérivaient l'une
    // de l'autre dès qu'un nom était plus haut qu'un autre : au-delà d'un
    // certain nombre de catégories, les dernières cases n'étaient plus en
    // face de leur nom (visible seulement une fois le panel limité en
    // hauteur avec ascenseur, qui a exposé un défaut déjà présent).
    const categoryRows: JSX.Element[] = [];
    for (let l = 0; l < videoCategories.length; l++) {
      const category = videoCategories[l];
      const test     = categoriesIds.includes(category.id);

      categoryRows.push(
        <div key={`toggle-${category.id}`}>
          <Toggle onChange={(e) => handleChoosenCategoryChange(e, category.id)} checked={test} />
        </div>
      );
      categoryRows.push(
        <div key={`name-${category.id}`} className={classNames(styles.name)}>{category.name}</div>
      );

      if (test) {
        nbChoosenCategories++;
        switch (nbChoosenCategories) {
          case 1:
            title = category.name;
            break;
          case 2:
            title += ", " + category.name;
            break;
          case 3:
            title += ", ...";
            break;
          default:
        }
      }
    }

return (
  <div className={classNames(styles.categoryWrapper)}>
    <div className={classNames(styles.collapsiblePanelWrapper)}>
      <CollapsiblePanel title={title} overlayOnOpen={true} maxHeight="200px">
        <div className={classNames(styles.toggles_list)}>
          {categoryRows}
        </div>
      </CollapsiblePanel>
    </div>
  </div>
);

  };

  const handleActiveChange = ( newState: boolean ) => {

    const fieldIsAlreadyModified = active !== initialVideo.active;
    setActive ( newState );

    if ( newState === initialVideo.active &&  fieldIsAlreadyModified ) { setNumberOfModifiedFields ( prev => Math.max(0, prev - 1)); }
    if ( newState !== initialVideo.active && !fieldIsAlreadyModified ) { setNumberOfModifiedFields ( prev => prev + 1);              }
  }

  const handleActiveScheduledChange = ( newState: boolean , id?: number | undefined) => {

    const fieldIsAlreadyModified = activeScheduled !== initialVideo.activeScheduled;
    setActiveScheduled (newState)

    if ( newState === initialVideo.activeScheduled &&  fieldIsAlreadyModified) { setNumberOfModifiedFields(prev => Math.max(0, prev - 1)); }
    if ( newState !== initialVideo.activeScheduled && !fieldIsAlreadyModified) { setNumberOfModifiedFields(prev => prev + 1);              }
  }

  const handleActiveStartDateChange = (e: React.ChangeEvent<HTMLInputElement> ) => {

    const newDate: string = e.target.value;
    const fieldIsAlreadyModified = activeStartDate !== initialVideo.activeStartDate;
    setActiveStartDate(convertStringToDate(newDate, 'dd/MM/yyyy HH:mm'));

    if (convertStringToDate(newDate) === initialVideo.activeStartDate && fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
    }
    if (convertStringToDate(newDate) !== initialVideo.activeStartDate && !fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => prev + 1);
    }
  }

  const handleActiveEndDateChange = (e: React.ChangeEvent<HTMLInputElement> ) => {

    const newDate: string = e.target.value;
    const fieldIsAlreadyModified = activeEndDate !== initialVideo.activeEndDate;
    setActiveEndDate(convertStringToDate(newDate, 'dd/MM/yyyy HH:mm'));

    if (convertStringToDate(newDate) === initialVideo.activeEndDate && fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
    }
    if (convertStringToDate(newDate) !== initialVideo.activeEndDate && !fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => prev + 1);
    }
  }

  const handleAutomaticPlaylistChange = ( newState: boolean ) => {

    const fieldIsAlreadyModified = automaticPlaylist !== initialVideo.automaticPlaylist;
    setAutomaticPlaylist (newState)

    if ( newState === initialVideo.automaticPlaylist &&  fieldIsAlreadyModified) { setNumberOfModifiedFields(prev => Math.max(0, prev - 1)); }
    if ( newState !== initialVideo.automaticPlaylist && !fieldIsAlreadyModified) { setNumberOfModifiedFields(prev => prev + 1);              }
  }

  const handlePublicPortalChange = ( newState: boolean ) => {

    const fieldIsAlreadyModified = publicPortal !== initialVideo.publicPortal;
    setPublicPortal (newState)

    if ( newState === initialVideo.publicPortal &&  fieldIsAlreadyModified) { setNumberOfModifiedFields(prev => Math.max(0, prev - 1)); }
    if ( newState !== initialVideo.publicPortal && !fieldIsAlreadyModified) { setNumberOfModifiedFields(prev => prev + 1);              }
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {

    const fieldIsAlreadyModified = title !== initialVideo.title;
    setTitle(e.target.value);
    setTitleErrorMessage(testTitle(e.target.value));

    if ( e.target.value === initialVideo.title && fieldIsAlreadyModified  ) { setNumberOfModifiedFields(prev => Math.max(0, prev - 1)); }
    if ( e.target.value !== initialVideo.title && !fieldIsAlreadyModified ) { setNumberOfModifiedFields(prev => prev + 1); }
  };

  const handleShortTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldIsAlreadyModified = shortTitle !== initialVideo.shortTitle;
    setShortTitle(e.target.value);

    if (e.target.value === initialVideo.shortTitle && fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
    }
    if (e.target.value !== initialVideo.shortTitle && !fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => prev + 1);
    }
  };


  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const fieldIsAlreadyModified = description !== initialVideo.description;
    setDescription(e.target.value);

    if (e.target.value === initialVideo.description && fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
    }
    if (e.target.value !== initialVideo.description && !fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => prev + 1);
    }
  };

  const handleDistributionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldIsAlreadyModified = distribution !== initialVideo.distribution;
    setDistribution(e.target.value);

    if (e.target.value === initialVideo.distribution && fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
    }
    if (e.target.value !== initialVideo.distribution && !fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => prev + 1);
    }
  };

  const handleProductionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldIsAlreadyModified = production !== initialVideo.production;
    setProduction(e.target.value);

    if (e.target.value === initialVideo.production && fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
    }
    if (e.target.value !== initialVideo.production && !fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => prev + 1);
    }
  };

  const handleDesignerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldIsAlreadyModified = designer !== initialVideo.designer;
    setDesigner(e.target.value);

    if (e.target.value === initialVideo.designer && fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
    }
    if (e.target.value !== initialVideo.designer && !fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => prev + 1);
    }
  };

  const handleCopyrightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldIsAlreadyModified = copyright !== initialVideo.copyright;
    setCopyright(e.target.value);

    if (e.target.value === initialVideo.copyright && fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
    }
    if (e.target.value !== initialVideo.copyright && !fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => prev + 1);
    }
  };

  const handleReleaseDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {

    const newDate: string = event.target.value;
    const fieldIsAlreadyModified = releaseDate !== initialVideo.releaseDate;
    setReleaseDate(convertStringToDate(newDate, 'dd/MM/yyyy'));
    setReleaseDateErrorMessage(testReleaseDate(newDate));

    if (convertStringToDate(newDate) === initialVideo.releaseDate && fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
    }
    if (convertStringToDate(newDate) !== initialVideo.releaseDate && !fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => prev + 1);
    }
  };

  const handleFirstUseDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {

    const newDate: string = event.target.value;
    const fieldIsAlreadyModified = firstUseDate !== initialVideo.firstUseDate;
    setFirstUseDate(convertStringToDate(newDate, 'dd/MM/yyyy'));

    if (convertStringToDate(newDate) === initialVideo.firstUseDate && fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
    }
    if (convertStringToDate(newDate) !== initialVideo.firstUseDate && !fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => prev + 1);
    }
  };



  const handleYoutubeIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldIsAlreadyModified = youtubeId !== `${initialVideo.youtubeId}`;
    setYoutubeId(e.target.value);

    if (e.target.value === `${initialVideo.youtubeId}` && fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
    }
    if (e.target.value !== `${initialVideo.youtubeId}` && !fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => prev + 1);
    }
  };

  const handleImageFileToUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event && event.target && event.target.files) {
      setImageFileToUpload(event.target.files[0]);
      setImageFileToUploadErrorMessage(testImageFileToUpload(event.target.files[0]));
      setNumberOfModifiedFields(prev => prev + 1);
    }
  };

  const handleVideoFileToUploadChange = ( event:      React.ChangeEvent<HTMLInputElement> ,
                                          value:      string | number | File | Date       ,
                                          mediaType?: string                              ,
                                          duration?:  string                              ) => {
    if (event && event.target && event.target.files) {

      if (duration) { setDuration (duration!) }
      setVideoFileToUpload(event.target.files[0]);
      setVideoFileToUploadErrorMessage(testVideoFileToUpload(event.target.files[0]));
      setNumberOfModifiedFields(prev => prev + 1);

      // Nouvelle vidéo (donc nouvelle durée) : les qualités précédemment choisies ne sont
      // plus pertinentes. Le choix sera redemandé au clic sur Créer/Enregistrer.
      setSelectedResolutions([]);
    }
  };

  // Refus d'un fichier de mauvais type : la fiche vidéo n'accepte qu'une image en
  // vignette et qu'une vidéo en aperçu. Rien n'est remonté au formulaire (ni fichier,
  // ni champ modifié), seul le message d'erreur du bloc concerné s'affiche.
  const handleImageFileRejected = (file: File) => {
    setImageFileToUploadErrorMessage(`« ${file.name} » n'est pas une image`);
  };

  const handleVideoFileRejected = (file: File) => {
    setVideoFileToUploadErrorMessage(`« ${file.name} » n'est pas une vidéo`);
  };

  const handleVideoUploadClick = () => {
    videoFileInputRef.current?.click();
  };

  // La poubelle ne fait pas la même chose selon ce qu'elle a devant elle.
  //
  // Un fichier fraîchement choisi, pas encore enregistré : elle défait le choix.
  // L'aperçu revient à la vignette de la fiche en modification, au vide en création.
  //
  // La vignette d'une fiche enregistrée : elle l'efface pour de bon. Ce n'est pas un
  // ordre envoyé à l'API — celle-ci ne sait pas effacer, l'absence de fichier y veut
  // dire « ne change rien » — c'est le logo qui part à sa place à l'enregistrement et
  // qui écrase `thumbnail.webp` et `thumbnail_BIG.webp` dans le répertoire du média.
  const handleImageDeleteClick = () => {
    if (imageFileToUpload !== null) {
      setImageFileToUpload(null);
      setImageSrc(imageUrl);
    } else {
      setImageSrc('');
      setImageUrl('');
      setImageDeletionRequested(true);
    }
    setImageFileToUploadErrorMessage('');
    setImagePreviewKey(previous => previous + 1);
    setNumberOfModifiedFields(previous => previous + 1);
  };

  // La vidéo, elle, ne s'efface pas : une fiche vidéo sans vidéo n'est pas
  // enregistrable (cf. le test de `save`), et le customer-front pointerait sur un
  // manifeste mort. La poubelle n'est donc offerte que sur un fichier choisi et pas
  // encore enregistré — retirer la vidéo d'une fiche, c'est supprimer la fiche.
  const handleVideoDeleteClick = () => {
    setVideoFileToUpload(null);
    setVideoFileToUploadErrorMessage('');
    setDuration(initialVideo.duration);
    setSelectedResolutions([]);
    setVideoPreviewKey(previous => previous + 1);
    setNumberOfModifiedFields(previous => previous + 1);
  };

  const handleImageUploadClick = () => {
    imageFileInputRef.current?.click();
  };

  const handleVideoQualitySelectionConfirm = (resolutions: HlsResolutionId[], compression: CompressionLevel) => {
    setSelectedResolutions(resolutions);
    setSelectedCompression(compression);
    compressionRef.current = compression;
    setVideoQualityWindowToShow(false);

    // Les qualités viennent d'être choisies mais `selectedResolutions` n'est pas
    // encore à jour : elles voyagent en argument jusqu'à save(), en passant par
    // la question d'activation si elle doit être posée.
    if (askActivationIfNeeded(resolutions)) return;

    setSaveButtonLock(true);
    save(resolutions);
  };

  const handleVideoQualitySelectionCancel = () => {
    setVideoQualityWindowToShow(false);
  };

  const arraysAreEqual = (arr1: number[], arr2: number[]) => {
    const sortedArr1 = arr1.slice().sort();
    const sortedArr2 = arr2.slice().sort();
    return sortedArr1.length === sortedArr2.length && sortedArr1.every((value, index) => value === sortedArr2[index]);
  };

  const handleChoosenCategoryChange = (newState: boolean, id: number) => {
    setCategoriesIds(prevCategoriesIds => {
      const fieldIsAlreadyModified = !arraysAreEqual(prevCategoriesIds, initialVideo.categoriesIds);
      let temp: number[] = [];

      if (newState) {
        temp = [...prevCategoriesIds, id].filter((value, index, self) => self.indexOf(value) === index);
      } else {
        temp = prevCategoriesIds.filter(categoryId => categoryId !== id);
      }

      if (arraysAreEqual(temp, initialVideo.categoriesIds) && fieldIsAlreadyModified) {
        setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
      }

      if (!arraysAreEqual(temp, initialVideo.categoriesIds) && !fieldIsAlreadyModified) {
        setNumberOfModifiedFields(prev => prev + 1);
      }

      setCategoriesIdsErrorMessage ( testCategoriesIds ( temp ) )

      return temp;
    });
  };

  const handleRequiredUserStatusIdChange = (newState: boolean, id: number) => {

    const fieldIsAlreadyModified = requiredUserStatusId !== initialVideo.requiredUserStatusId;

    setRequiredUserStatusId(id);
    setRequiredUserStatusIdErrorMessage ('')

    if (id === initialVideo.requiredUserStatusId &&  fieldIsAlreadyModified) { setNumberOfModifiedFields(prev => Math.max(0, prev - 1));  }
    if (id !== initialVideo.requiredUserStatusId && !fieldIsAlreadyModified) { setNumberOfModifiedFields(prev => prev + 1);  }
  };

  const PAID_STATUS_ID = 5;
  const isPaidStatus = requiredUserStatusId === PAID_STATUS_ID;
  
  const handleRequiredUserStatusSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleRequiredUserStatusIdChange(true, parseInt(e.target.value, 10));
  };

  // -1 : commentaires fermés ; 0 à 3 : niveau de l'IA (Légère, Standard, Avancée, Manuelle).
  const computeCommentsModerationValue = (allowed: boolean, level: string): number =>
    !allowed ? -1 : parseInt(level, 10);

  const handleCommentsAllowedChange = (newState: boolean) => {
    const currentValue = computeCommentsModerationValue(commentsAllowed, commentsModerationLevel);
    const newValue      = computeCommentsModerationValue(newState,       commentsModerationLevel);

    const fieldIsAlreadyModified = currentValue !== initialVideo.commentsModeration;
    setCommentsAllowed(newState);

    if ( newValue === initialVideo.commentsModeration &&  fieldIsAlreadyModified) { setNumberOfModifiedFields(prev => Math.max(0, prev - 1)); }
    if ( newValue !== initialVideo.commentsModeration && !fieldIsAlreadyModified) { setNumberOfModifiedFields(prev => prev + 1);              }
  };

  const handleCommentsModerationLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLevel = e.target.value;
    const currentValue = computeCommentsModerationValue(commentsAllowed, commentsModerationLevel);
    const newValue      = computeCommentsModerationValue(commentsAllowed, newLevel);

    const fieldIsAlreadyModified = currentValue !== initialVideo.commentsModeration;
    setCommentsModerationLevel(newLevel);

    if ( newValue === initialVideo.commentsModeration &&  fieldIsAlreadyModified) { setNumberOfModifiedFields(prev => Math.max(0, prev - 1)); }
    if ( newValue !== initialVideo.commentsModeration && !fieldIsAlreadyModified) { setNumberOfModifiedFields(prev => prev + 1);              }
  };

  const handleTagListChange = (tags: Keyword[]) => {
    setUsedKeywords(tags);
    setKeywordsIds(prevKeywordsIds => {
      const fieldIsAlreadyModified = !arraysAreEqual(prevKeywordsIds, initialVideo.keywordsIds);
      let temp: number[] = tags.map(keyword => keyword.id);

      if (arraysAreEqual(temp, initialVideo.keywordsIds) && fieldIsAlreadyModified) {
        setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
      }

      if (!arraysAreEqual(temp, initialVideo.keywordsIds) && !fieldIsAlreadyModified) {
        setNumberOfModifiedFields(prev => prev + 1);
      }

      return temp;
    });
  };

  const handleSoldProductPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {

    const fieldIsAlreadyModified = soldProductPrice !== initialSoldProductPrice;

    setSoldProductPrice(e.target.value);

    if (e.target.value === initialSoldProductPrice && fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => Math.max(0, prev - 1));
    }
    if (e.target.value !== initialSoldProductPrice && !fieldIsAlreadyModified) {
      setNumberOfModifiedFields(prev => prev + 1);
    }
  };


  const handleTagCreate = async (keyword: Keyword): Promise<number> => {
    const response = await createKeyword(keyword.word);
    return response.code === 1 ? response.payload : 0;
  };

  /**
   * Dernier arrêt avant l'enregistrement.
   *
   * On ne pose la question que pour une création, vidéo non active, ET avec un
   * fichier à transcoder : sans fichier il n'y a pas de fin de transcodage, un
   * « oui » ne se déclencherait jamais et la question serait un piège.
   *
   * Retourne true si la question a été posée — l'appelant s'arrête alors, la
   * sauvegarde repart depuis la réponse.
   */
  const askActivationIfNeeded = (resolutions?: HlsResolutionId[]): boolean => {
    if (isEditMode || active || (videoFileToUpload === null && !prefillFromRecording)) return false;

    // Création guidée : la réponse est déjà à l'écran, inutile d'interrompre par une fenêtre.
    if (onGuideState) {
      setSaveButtonLock(true);
      save(resolutions, publishModeRef.current === 'now');
      return true;
    }

    pendingResolutionsRef.current = resolutions;
    setActivationQuestionToShow(true);
    return true;
  };

  /** Oui comme Non poursuivent la création : seule la réponse change. */
  const continueSaveWithActivationAnswer = (activateAfterTranscoding: boolean) => {
    setActivationQuestionToShow(false);
    setSaveButtonLock(true);
    save(pendingResolutionsRef.current, activateAfterTranscoding);
  };

  const handleSaveButton = () => {

    if (saveButtonLock) return;

    if (onGuideState && !isEditMode) {
      const message = publishModeRef.current === null                      ? 'Indiquez quand la vidéo doit sortir.'
                    : publishModeRef.current === 'now'                     ? ''
                    : !activeStartDate || isNaN(activeStartDate.getTime()) ? 'Indiquez la date et l\'heure de sortie.'
                    : activeStartDate.getTime() <= Date.now()              ? 'La date de sortie doit être dans le futur.'
                    :                                                        '';
      setPublishError(message);
      if (message !== '') return;
    }

    // Nouvelle vidéo sans qualités choisies : on demande d'abord les qualités à générer
    // (estimation de taille vs quota) avant de lancer la sauvegarde.
    if ((videoFileToUpload !== null || !!prefillFromRecording) && selectedResolutions.length === 0)
     {
      setVideoQualityWindowToShow(true);
      return;
     }

    if (askActivationIfNeeded()) return;

    setSaveButtonLock(true);
    save();
  };


  const handleCreationAckButton = () => {
    setSaved(false);
    if ( uploadProgressFunction) uploadProgressFunction(0);
    setSaveButtonLock(false);

    if (isEditMode) {
      if (onEdit) {
        onEdit(videoSheetId);
      }
    } else {
      if (onCreate) {
        onCreate(videoSheetId);
      }
    }
  };


  const handleErrorAckButton = () => {
    setSaved(false);
    if ( uploadProgressFunction) uploadProgressFunction(0);
    setSaveButtonLock(false);
    window.scroll(0, 0);
  };

  const handleCancelButton2 = () => {
    setCancelation(false);
    setSaved(false);
    if ( uploadProgressFunction) uploadProgressFunction(0);
    setSaveButtonLock(false);
    window.scroll(0, 0);
  };

  const handleValidButton2 = () => {
    setCancelation(false);

    if (isEditMode) {
      if (onEdit) {
        onEdit(0);
      }
    } else {
      if (onCreate) {
        onCreate(0);
      }
    }
  };

  const handleCancelButton3 = () => {
    setCancelation(true);
  };

  const handleYoutubeIdForRetrievingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setYoutubeIdForRetrieving(e.target.value);
  };

  const handleInformationsRetrieveFromYoutubeClick = () => {
    retrieveInformationsFromYoutube();
  };

  const handleErrorYoutubeRefAckButton = () => {
	setErrorYoutubeRef (false)
  }

  const handleMagicWandClick = () => {
  }

  // Création guidée : seuls l'aperçu, le nom, la description et la catégorie sont d'emblée à
  // l'écran. Les autres blocs restent montés mais cachés, avec leurs valeurs par défaut, et
  // s'ouvrent un par un à la demande.
  const guided = !!onGuideState && !isEditMode;
  const [ openOptions      , setOpenOptions      ] = useState<Set<string>>(new Set());
  // La sortie est une question posée, sans réponse d'avance : une vidéo ne doit pas devenir
  // publique par simple oubli. Les deux réponses la destinent au portail public.
  const [ publishMode      , setPublishMode      ] = useState<'now' | 'scheduled' | null>(null);
  const [ publishError     , setPublishError     ] = useState('');
  publishModeRef.current = publishMode;

  const handlePublishModeChange = (mode: 'now' | 'scheduled') => {
    setPublishMode(mode);
    setPublishError('');
    setPublicPortal(true);
    setActiveScheduled(mode === 'scheduled');
    if (mode === 'now') setActiveStartDate(null);
  };

  const handleOptionToggle = (key: string) => {
    setOpenOptions(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Chaque option affiche sa valeur du moment : on voit ce qui s'appliquera sans rien ouvrir.
  const networksCount = Object.values(socialNetworksToPublishAtCreation).filter(Boolean).length;
  const creditsFilled = !!(shortTitle || distribution || production || copyright || designer || firstUseDate);
  const guidedOptions = [
    { key: 'thumbnail'  , label: 'Vignette'                , value: imageFileToUpload ? 'Image choisie' : 'Automatique' },
    { key: 'keywords'   , label: 'Mots-clés'               , value: usedKeywords.length > 0 ? `${usedKeywords.length} choisi${usedKeywords.length > 1 ? 's' : ''}` : 'Aucun' },
    { key: 'access'     , label: 'Accès et prix'           , value: availableUserStatus.find(status => status.id === requiredUserStatusId)?.name ?? '' },
    { key: 'publication', label: 'Portail et commentaires' , value: `${publicPortal ? 'Visible' : 'Hors portail'}, commentaires ${commentsAllowed ? 'ouverts' : 'fermés'}` },
    { key: 'networks'   , label: 'Réseaux sociaux'         , value: networksCount > 0 ? `${networksCount} choisi${networksCount > 1 ? 's' : ''}` : 'Aucun' },
    { key: 'credits'    , label: 'Crédits et dates'        , value: creditsFilled ? 'Renseignés' : 'Non renseignés' },
    { key: 'youtube'    , label: 'YouTube'                 , value: youtubeId ? 'Lié' : 'Non lié' },
  ];

  const optionStyle = (key: string, base?: React.CSSProperties): React.CSSProperties | undefined => {
    if (!guided) return base;
    return { ...base, order: 12 + 2 * guidedOptions.findIndex(o => o.key === key), ...(openOptions.has(key) ? {} : { display: 'none' }) };
  };

  // Une erreur ne doit jamais rester dans un bloc caché : on ouvre celui qui la porte.
  useEffect(() => {
    if (!guided) return;
    const toOpen: string[] = [];
    if (requiredUserStatusIdErrorMessage !== '') toOpen.push('access');
    if (imageFileToUploadErrorMessage !== '')    toOpen.push('thumbnail');
    if (toOpen.length > 0) setOpenOptions(prev => new Set([...Array.from(prev), ...toOpen]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredUserStatusIdErrorMessage, imageFileToUploadErrorMessage]);

  // Création guidée : MediaSheet suit le nom et la catégorie pour savoir quelle étape proposer.
  useEffect(() => {
    if (onGuideState) onGuideState({ title, categoryChosen: categoriesIds.length > 0, releaseChosen: publishMode !== null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, categoriesIds, publishMode]);

  const requiredStatusChoices = availableUserStatus.map(s => `${s.id}`);
  const requiredStatusLabels  = availableUserStatus.map(s => s.name);

  return (

    <div className={classNames(styles.root, guided && styles.root_guided)}>

      { 
       showWaitingIcon && 
       <>
        <div className={classNames(styles.overlay)} />
        <AnimatedWaitingIcon2 />
       </>
      }
			  
      {
       categoryCreationWindowToShow  &&
       (
        <Box width     = "1500px"
             maxHeight = "700px"
             onClose   = {handleCategoryCreationWindowClose}
             marginTop = "0px" >
          <MediaCategoryManager embedded onCategoryCreated={handleCategoryCreated}/>
        </Box>
       )
      }

      {
       videoQualityWindowToShow &&
       (
        <Box width     = "680px"
             onClose    = {handleVideoQualitySelectionCancel}
             marginTop  = "0px" >
          <VideoResolutionSelector durationSeconds     = {convertTimeToSeconds(duration)}
                                    quotaRemainingBytes = {octetsRestants(authentication.admin?.quotaUsed ?? 0, authentication.admin?.quotaDisk ?? 0)}
                                    initialSelected     = {selectedResolutions}
                                    initialCompression  = {selectedCompression}
                                    source4K            = {sourceIs4K}
                                    onConfirm           = {handleVideoQualitySelectionConfirm}
                                    onCancel            = {handleVideoQualitySelectionCancel} />
        </Box>
       )
      }

      {
       !saved &&
       progress > 0 &&
       (
        <Window message     = {
                               <>
                                 Les éléments de la fiche sont en cours d'upload<br />
                                 {`${ (progress * 0.9).toFixed(0) }%`} effectués
                               </>
                              }
                messageType = "information" />
       )
      }

      {
       saved &&
       error &&
       (
        <Window message           = {
                                     isEditMode
                                     ?
                                     <>Une erreur est survenue lors de la modification de la fiche</>
                                     :
                                     <>Une erreur est survenue lors de la création de la fiche</>
                                    }
          messageType             = "information"
          validButtonText         = "Ok"
          onClickOnValidateButton = {handleErrorAckButton} />
       )
      }

      {
       saved &&
       !error &&
       (
        <Window message                 = {
                                           isEditMode
                                           ?
                                           <>Fiche modifiée</>
                                           :
                                           <>Fiche créée</>
                                          }
                messageType             = "information"
                validButtonText         = "Ok"
                onClickOnValidateButton = {handleCreationAckButton}
        />
       )
      }

      {
       socialNetworkToDisconnect &&
       (
        <Window message                 = {<>{`Déconnecter le compte ${socialNetworkToDisconnect.name}${socialNetworkToDisconnect.externalAccountLabel ? ` (${socialNetworkToDisconnect.externalAccountLabel})` : ''} ? Vous pourrez ensuite en connecter un autre.`}</>}
                messageType             = "question"
                validButtonText         = "Oui"
                cancelButtonText        = "Non"
                onClickOnCancelButton   = {() => setSocialNetworkToDisconnect(null)}
                onClickOnValidateButton = {handleDisconnectSocialNetwork} />
       )
      }

      {
       cancelation &&
       (
        <Window message                 = {<>Etes-vous sûr de vouloir annuler vos saisies ?</>}
                messageType             = "question"
                validButtonText         = "Oui"
                cancelButtonText        = "Non"
                onClickOnCancelButton   = {handleCancelButton2}
                onClickOnValidateButton = {handleValidButton2} />
       )
      }

      {
       activationQuestionToShow &&
       (
        <Window message                 = {<>La vidéo n'a pas le statut "actif", voulez-vous l'activer à l'issue de son chargement ?</>}
                messageType             = "question"
                validButtonText         = "Oui"
                cancelButtonText        = "Non"
                onClickOnCancelButton   = {() => continueSaveWithActivationAnswer(false)}
                onClickOnValidateButton = {() => continueSaveWithActivationAnswer(true)} />
       )
      }

      {
       errorYoutubeRef &&
       (
        <Window message                 = {<>Référence YouTube invalide ou introuvable</>}
                messageType             = "information"
                validButtonText         = "Ok"
                onClickOnValidateButton = {handleErrorYoutubeRefAckButton} />
       )
      }

      <div className={classNames(styles.contain)}>

        {
         isEditMode &&
         (() => {
           // Même contenu à deux endroits possibles, comme la barre de boutons
           // plus bas : projeté dans la ligne d'en-tête de MediaSheet quand elle
           // ouvre un emplacement, en tête du formulaire sinon. Dans l'en-tête il
           // se réduit à son contenu au lieu d'occuper une ligne entière.
           const bandeau = (
             <div className={classNames(styles.topBar, metaSlot && styles.topBar_in_header)}>
               <WidgetFrame name               = "Id :"
                            error              = ""
                            labelTextPosition  = "right"
                            labelBlockPosition = "left" >
                 <p className = {classNames(styles.text)}>{id}</p>
               </WidgetFrame>
               <WidgetFrame name               = "Uuid :"
                            error              = ""
                            labelTextPosition  = "right"
                            labelBlockPosition = "left" >
                 <p className = {classNames(styles.text)}>{uuid}</p>
               </WidgetFrame>
               <Label value = {`Date de création : ${convertDateToString(creationDate, 'dd/MM/yyyy HH:mm')}`} />
               <CreationIp ipAddress = {creationIpAddress} />
             </div>
           );

           return metaSlot ? createPortal(bandeau, metaSlot) : bandeau;
         })()
        }

        <div className={classNames(styles.columns, guided && styles.columns_guided)}>

          {/* ===== Ligne 1 : Information principale / Titre court et diffusion / Publication et diffusion ===== */}
          <div className={classNames(styles.block)} data-guide="title" style={guided ? { order: 1 } : undefined}>
            {!guided && <div className={classNames(styles.blockTitle)}>Information principale</div>}

            <WidgetFrame name               = {guided ? "1. Nom de la vidéo" : "Titre :"}
			               error              = {titleErrorMessage}
						   labelTextPosition  = "left"
						   labelBlockPosition = "top"
						   classNameGrid      = {classNames(styles.grid_frame)}>
              <Field type        = "text"
				       placeholder = ""
					   value       = {title}
					   onChange    = {handleTitleChange} />
            </WidgetFrame>

            <WidgetFrame name               = {guided ? "Description (facultatif) :" : "Description :"}
			               error              = {descriptionErrorMessage}
						   labelTextPosition  = "left"
						   labelBlockPosition = "top"
						   classNameGrid      = {classNames(styles.grid_frame_description)}>
              <TextArea placeholder = ""
				          value       = {description}
						  onChange    = {handleDescriptionChange}
						  height      = "100%" />
            </WidgetFrame>
          </div>

          <div className={classNames(styles.block)} style={optionStyle('credits')}>
            <div className={classNames(styles.blockTitle)}>Titre court et diffusion</div>

            <WidgetFrame name               = "Titre court :"
			               error              = ""
						   labelTextPosition  = "left"
						   labelBlockPosition = "top"
						   classNameGrid      = {classNames(styles.grid_frame)}>
              <Field type        = "text"
				       placeholder = ""
					   value       = {shortTitle}
					   onChange    = {handleShortTitleChange} />
            </WidgetFrame>

            <div className={classNames(styles.pairRow)}>
              <WidgetFrame name                  = "Date de création"
                           labelTextPosition     = "left"
                           labelBlockPosition    = "top"
						   classNameGrid      = {classNames(styles.grid_frame)}>
                <FieldCalendar value      = {releaseDate ? convertDateToString(releaseDate, 'dd/MM/yyyy') : ''}
                               onChange   = {handleReleaseDateChange}
                               checkInput = {false}
                               showHours  = {false}
                               dateFormat = "dd/MM/yyyy" />
                {/*
                 releaseDateErrorMessage !== '' &&
                 <p className={classNames(styles.alert)}>{releaseDateErrorMessage}</p>
                */}
              </WidgetFrame>
              <WidgetFrame name                  = "Date de diffusion"
                           labelTextPosition     = "left"
                           labelBlockPosition    = "top"
						   classNameGrid      = {classNames(styles.grid_frame)}>
                <FieldCalendar value      = {firstUseDate ? convertDateToString(firstUseDate, 'dd/MM/yyyy') : ''}
                               onChange   = {handleFirstUseDateChange}
                               checkInput = {false}
                               showHours  = {false}
                               dateFormat = "dd/MM/yyyy" />
              </WidgetFrame>
            </div>

            <div className={classNames(styles.pairRow)}>
              <WidgetFrame name               = "Distributeur :"
				             error              = ""
							 labelTextPosition  = "left"
							 labelBlockPosition = "top"
						   classNameGrid      = {classNames(styles.grid_frame)}>
                <Field type        = "text"
				         placeholder = ""
						 value       = {distribution}
						 onChange    = {handleDistributionChange} />
              </WidgetFrame>
              <WidgetFrame name               = "Producteur :"
				             error              = ""
							 labelTextPosition  = "left"
							 labelBlockPosition = "top"
						     classNameGrid      = {classNames(styles.grid_frame)}>
                <Field type        = "text"
				         placeholder = ""
						 value       = {production}
						 onChange    = {handleProductionChange} />
              </WidgetFrame>
            </div>

            <div className={classNames(styles.pairRow)}>
              <WidgetFrame name="Copyright :"
			                 error=""
						     labelTextPosition="left"
						     labelBlockPosition="top"
						     classNameGrid      = {classNames(styles.grid_frame)}>
                <Field type="text" placeholder="" value={copyright} onChange={handleCopyrightChange} />
              </WidgetFrame>
              <WidgetFrame name               = "Concepteur :"
				             error              = ""
							 labelTextPosition  = "left"
							 labelBlockPosition = "top"
							 classNameGrid      = {classNames(styles.grid_frame)}>
                <Field type        = "text"
				         placeholder = ""
						 value       = {designer}
						 onChange    = {handleDesignerChange} />
              </WidgetFrame>
            </div>

          </div>

          <div className={classNames(styles.block)} style={optionStyle('publication')}>
            <div className={classNames(styles.blockTitle)}>Publication et diffusion</div>

            <div className={classNames(styles.publicationRow)} style={guided ? { display: 'none' } : undefined}>
              <WidgetFrame name               = "Actif :"
                           labelTextPosition  = "right"
                           labelBlockPosition = "left" >
							 
				  <div className={styles.row}>			 
                  <Toggle checked  = {active}
                          form     = "switch"
                          onChange = {handleActiveChange}/>

                  {
                   active &&
                   (
                    <Toggle form    = "switch"
                            checked = {activeScheduled}
                            textOn  = "Programmé"
                            textOff = "Immédiat"
                            show    = "text"
							  textColorOn = "#FFFFFF"
							  textColorOff = "#FFFFFF"
                            fillColorOn = "#6632FF"
                            fillColorOff = "#6632FF"
                            internalPosition = {false}
                            persistentIcons = {true}
                            onChange = {handleActiveScheduledChange} />
                   )  
                  }
				  </div>			 

              </WidgetFrame>


                  {
                   activeScheduled &&
                   (
                    <div className={classNames(styles.pairRow)}>
                      <WidgetFrame name                  = "Date de début :"
                                   labelTextPosition     = "left"
                                   labelBlockPosition    = "top" 
						             classNameGrid         = {classNames(styles.grid_frame)}>
                        <FieldCalendar value      = {activeStartDate ? convertDateToString(activeStartDate, 'dd/MM/yyyy HH:mm') : ''}
                                       onChange   = {handleActiveStartDateChange}
                                       showHours  = {true}
                                       checkInput = {false}
                                       width      = "100%"
                                       dateFormat = "dd/MM/yyyy HH:mm" />
                      </WidgetFrame>
                      <WidgetFrame name                  = "Date de fin :"
                                   labelTextPosition     = "left"
                                   labelBlockPosition    = "top" 
						             classNameGrid      = {classNames(styles.grid_frame)}>
                        <FieldCalendar value      = {activeEndDate ? convertDateToString(activeEndDate, 'dd/MM/yyyy HH:mm') : ''}
                                       onChange   = {handleActiveEndDateChange}
                                       showHours  = {true}
                                       checkInput = {false}
                                       width      = "100%"
                                       dateFormat = "dd/MM/yyyy HH:mm" />
                      </WidgetFrame>
                    </div>
                   )
                  }
            </div>

            <WidgetFrame name               = "Visible sur le portail public :"
			               error              = ""
						   labelTextPosition  = "left"
						   labelBlockPosition = "left"
						   labelWidth         = '230px' 
						   classNameGrid      = {classNames(styles.grid_frame)}>
              <Toggle checked  = {publicPortal}
                      form     = "switch"
                      onChange = {handlePublicPortalChange} />
            </WidgetFrame>

            <WidgetFrame name               = "Programmation automatique :"
			               error              = ""
						   labelTextPosition  = "left"
						   labelBlockPosition = "left"
						   labelWidth         = '230px' 
						   classNameGrid      = {classNames(styles.grid_frame)}>
              <Toggle checked  = {automaticPlaylist}
                      form     = "switch"
                      onChange = {handleAutomaticPlaylistChange} />
            </WidgetFrame>

            {/* Un seul réglage sur une ligne : ouvrir les commentaires, et à côté le
                niveau de l'IA qui les trie (comme le chat d'une chaîne). */}
            <WidgetFrame name               = "Autoriser les commentaires :"
			               error              = ""
						   labelTextPosition  = "left"
						   labelBlockPosition = "left"
						   labelWidth         = '230px' 
						   classNameGrid      = {classNames(styles.grid_frame)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Toggle checked  = {commentsAllowed}
                        form     = "switch"
                        onChange = {handleCommentsAllowedChange} />
                {
                 commentsAllowed &&
                 (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 180 }}
                       title={CHAT_MODERATION_LEVELS.find(l => String(l.value) === commentsModerationLevel)?.hint}>
                    <span style={{ whiteSpace: 'nowrap', opacity: 0.8 }}>Modération :</span>
                    <Select choices         = {commentsModerationChoices}
                            choicesLabel    = {commentsModerationLabels}
                            value           = {commentsModerationLevel}
                            onChange        = {handleCommentsModerationLevelChange}
                            showPlaceholder = {false}
                            width           = "100%" />
                  </div>
                 )
                }
              </div>
            </WidgetFrame>

          </div>

          {guided && (
            <div className={classNames(styles.block)} style={{ order: 3 }} data-guide="release">
              <div className={classNames(styles.guidedTitle)} id="videoReleaseTitle">3. Quand doit-elle sortir ?</div>
              <div className={classNames(styles.releaseCards)} role="group" aria-labelledby="videoReleaseTitle">
                {([
                  { mode: 'now'       as const, label: 'Tout de suite'         , hint: "Visible par tous dès qu'elle est prête" },
                  { mode: 'scheduled' as const, label: 'À une date programmée' , hint: "Choisissez le jour et l'heure" },
                ]).map(card => (
                  <button key={card.mode} type="button" aria-pressed={publishMode === card.mode}
                          className={classNames(styles.releaseCard, publishMode === card.mode && styles.releaseCard_on)}
                          onClick={() => handlePublishModeChange(card.mode)}>
                    <span className={classNames(styles.releaseCardLabel)}>{card.label}</span>
                    <span className={classNames(styles.releaseCardHint)}>{card.hint}</span>
                  </button>
                ))}
              </div>
              {publishMode === 'scheduled' && (
                <div style={{ maxWidth: 280 }}>
                  <WidgetFrame name               = "Date et heure de sortie :"
                               labelTextPosition  = "left"
                               labelBlockPosition = "top"
                               classNameGrid      = {classNames(styles.grid_frame)}>
                    <FieldCalendar value      = {activeStartDate ? convertDateToString(activeStartDate, 'dd/MM/yyyy HH:mm') : ''}
                                   onChange   = {handleActiveStartDateChange}
                                   showHours  = {true}
                                   checkInput = {false}
                                   width      = "100%"
                                   dateFormat = "dd/MM/yyyy HH:mm" />
                  </WidgetFrame>
                  <span style={{ fontSize: 13, opacity: 0.8 }}>Heure de votre appareil</span>
                </div>
              )}
              {publishError !== '' && <p className={classNames(styles.alert)} role="alert">{publishError}</p>}
            </div>
          )}

          {guided && <div className={classNames(styles.optionsHeading)} style={{ order: 10 }}>Options facultatives <span>déjà réglées, à changer seulement si besoin</span></div>}
          {guided && guidedOptions.map((option, index) => (
            <button key={option.key} type="button" aria-expanded={openOptions.has(option.key)}
                    className={classNames(styles.optionRow)} style={{ order: 11 + 2 * index }}
                    onClick={() => handleOptionToggle(option.key)}>
              <span>{option.label}</span>
              <span className={classNames(styles.optionValue)}>
                {option.value} <span className={classNames(styles.optionSign)} aria-hidden="true">{openOptions.has(option.key) ? '−' : '+'}</span>
              </span>
            </button>
          ))}

          {/* ===== Ligne 2 : aperçus vignette+vidéo / Classification / Publier sur les réseaux ===== */}
          <div className={classNames(guided && styles.guidedPreview)} style={guided ? undefined : { display: 'flex', gap: 16, width: '100%' }}>
          <div className={classNames(styles.block)} style={guided ? { order: 1, display: openOptions.has('thumbnail') ? undefined : 'none' } : { flex: '1 1 0', minWidth: 0 }}>
            <div className={classNames(styles.blockHeader)}>
              <div className={classNames(styles.blockTitle)}>Aperçu vignette</div>
              {
               originalImageName !== '' &&
               <div className={classNames(styles.blockTitleFile)} title={originalImageName}>{originalImageName}</div>
              }
              {/* Même commande que pour l'aperçu vidéo juste en dessous : remplacer la
                  vignette d'une fiche enregistrée était possible mais invisible, il
                  fallait deviner que l'aperçu lui-même ouvrait le sélecteur. */}
              <div className={classNames(styles.blockHeaderButtons)}>
                <div className  = {classNames(styles.blockHeaderButton)}
                     onClick    = {handleImageUploadClick}
                     title      = {imageSrc !== '' ? "Remplacer la vignette" : "Choisir une vignette"}
                     role       = "button"
                     tabIndex   = {0}
                     onKeyDown  = {(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleImageUploadClick(); } }} >
                  <Icon name       = "upload"
                        color      = "var(--color-Field-Icon-normal)"
                        hoverColor = "var(--color-Field-Icon-hover-normal)"
                        width      = {18}
                        height     = {18} />
                </div>
                {
                 (imageFileToUpload !== null || imageSrc !== '') &&
                 <div className  = {classNames(styles.blockHeaderButton)}
                      onClick    = {handleImageDeleteClick}
                      title      = {imageFileToUpload !== null ? "Retirer la vignette choisie" : "Supprimer la vignette (le logo prendra sa place)"}
                      role       = "button"
                      tabIndex   = {0}
                      onKeyDown  = {(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleImageDeleteClick(); } }} >
                   <Icon name       = "trashCan"
                         color      = "var(--color-Field-Icon-normal)"
                         hoverColor = "var(--color-Field-Icon-hover-normal)"
                         width      = {18}
                         height     = {18} />
                 </div>
                }
              </div>
            </div>
            <FieldMedia key           = {imagePreviewKey}
                        placeholder   = ""
                        value         = {imageSrc}
						  showTextInput = {false}
						  upload        = {false}
						  previewAlwaysShown = {true}
						  previewAspectRatio = {!isEditMode ? "16/9" : undefined}
						  forceType     = "Image"
                        fileInputRef  = {imageFileInputRef}
                        onChange      = {handleImageFileToUploadChange}
                        onFileRejected = {handleImageFileRejected}/>
            {
             imageFileToUploadErrorMessage !== '' &&
             <p className={classNames(styles.alert)}>{imageFileToUploadErrorMessage}</p>
            }
          </div>

          <div className={classNames(styles.block)} style={guided ? undefined : { flex: '1 1 0', minWidth: 0 }}>
            <div className={classNames(styles.blockHeader)}>
              <div className={classNames(styles.blockTitle)}>Aperçu vidéo</div>
              {
               originalVideoName !== '' &&
               <div className={classNames(styles.blockTitleFile)} title={originalVideoName}>{originalVideoName}</div>
              }
              {/* Remplacer la vidéo d'une fiche déjà enregistrée est un geste normal,
                  mais il n'avait aucune commande : il fallait deviner que l'aperçu
                  lui-même ouvrait le sélecteur. Le bouton le rend visible, sans rien
                  changer au chemin d'enregistrement — updateVideo relance l'upload par
                  morceaux dès que videoFileToUpload n'est plus nul. */}
              {!prefillFromRecording && (
              <div className={classNames(styles.blockHeaderButtons)}>
                <div className  = {classNames(styles.blockHeaderButton)}
                     onClick    = {handleVideoUploadClick}
                     title      = {videoSrc !== '' ? "Remplacer la vidéo" : "Choisir une vidéo"}
                     role       = "button"
                     tabIndex   = {0}
                     onKeyDown  = {(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleVideoUploadClick(); } }} >
                  <Icon name       = "upload"
                        color      = "var(--color-Field-Icon-normal)"
                        hoverColor = "var(--color-Field-Icon-hover-normal)"
                        width      = {18}
                        height     = {18} />
                </div>
                {
                 videoFileToUpload !== null &&
                 <div className  = {classNames(styles.blockHeaderButton)}
                      onClick    = {handleVideoDeleteClick}
                      title      = "Retirer la vidéo choisie"
                      role       = "button"
                      tabIndex   = {0}
                      onKeyDown  = {(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleVideoDeleteClick(); } }} >
                   <Icon name       = "trashCan"
                         color      = "var(--color-Field-Icon-normal)"
                         hoverColor = "var(--color-Field-Icon-hover-normal)"
                         width      = {18}
                         height     = {18} />
                 </div>
                }
              </div>
              )}
            </div>
            {prefillFromRecording ? (
              // Source déjà sur le disque du serveur — aucun sélecteur, rien à
              // remplacer : le fichier est celui de l'enregistrement, point final.
              // previewUrl vient du même mécanisme que le bouton ▶ de
              // RecordingStorageModal (resolveFileUrl, mode 'inline') : la
              // lecture ne passe jamais par un upload.
              <>
                <div className={classNames(styles.blockTitleFile)} style={{ padding: '8px 0' }}>
                  Enregistrement de régie : {prefillFromRecording.filename}.{prefillFromRecording.format}
                </div>
                {(prefillFromRecording.angleCount ?? 0) > 0 && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={publishAngles} onChange={e => setPublishAngles(e.target.checked)} />
                    Publier en multiview ({prefillFromRecording.angleCount} angle{prefillFromRecording.angleCount === 1 ? '' : 's'} de la même prise)
                  </label>
                )}
                {prefillFromRecording.previewUrl && (
                  <FieldMedia value              = {prefillFromRecording.previewUrl}
                              showTextInput      = {false}
                              upload             = {false}
                              readOnly           = {true}
                              previewAlwaysShown = {true}
                              forceType          = "Video" />
                )}
              </>
            ) : (
              <>
                <FieldMedia key           = {videoPreviewKey}
                            placeholder   = ""
                            value         = {videoSrc}
                            showTextInput = {false}
                            upload        = {false}
                            previewAlwaysShown = {true}
                            previewAspectRatio = {!isEditMode ? "16/9" : undefined}
                            forceType     = "Video"
                            fileInputRef  = {videoFileInputRef}
                            onChange      = {handleVideoFileToUploadChange}
                            onFileRejected = {handleVideoFileRejected} />
                {
                 videoFileToUploadErrorMessage !== '' &&
                 <p className={classNames(styles.alert)}>{videoFileToUploadErrorMessage}</p>
                }
              </>
            )}
            {
             // Même adresse que la liste des médias (MediaRow) : celle du portail public. Elle
             // reste affichée quelle que soit l'activation — c'est l'adresse de la vidéo.
             isEditMode && id > 0 && initialVideo.slug &&
             (
              <a href      = {`${WWW_URLS.portal_front}/${initialVideo.slug}`}
                 target    = "_blank"
                 rel       = "noopener noreferrer"
                 className = {classNames(styles.directLink)}>
                {`${WWW_URLS.portal_front}/${initialVideo.slug}`}
              </a>
             )
            }
          </div>
          </div>

          <div className={classNames(styles.block)} style={guided ? { order: 2 } : undefined}>
            {guided
              ? <div className={classNames(styles.guidedTitle)} id="videoCategoryTitle">2. Catégorie</div>
              : <div className={classNames(styles.blockTitle)}>Classification</div>}

            {guided ? (
              <div className={classNames(styles.categoryChips)} role="group" aria-labelledby="videoCategoryTitle" data-guide="category">
                {videoCategories.map(category => {
                  const selected = categoriesIds.includes(category.id);
                  return (
                    <button key={category.id} type="button" aria-pressed={selected}
                            className={classNames(styles.categoryChip, selected && styles.categoryChip_on)}
                            onClick={() => handleChoosenCategoryChange(!selected, category.id)}>
                      {category.name}
                    </button>
                  );
                })}
                {categoriesLoaded && videoCategories.length === 0 && (
                  <p className={classNames(styles.alert)}>Vous n'avez encore aucune catégorie : créez-en une depuis la gestion des catégories de la page Vidéo.</p>
                )}
                {categoriesIdsErrorMessage !== '' && <p className={classNames(styles.alert)} role="alert">{categoriesIdsErrorMessage}</p>}
              </div>
            ) : (
            <div className={classNames(styles.classificationGrid)} data-guide="category">

              <WidgetFrame name               = {"Catégorie :"}
                           error              = {categoriesIdsErrorMessage}
                           labelTextPosition  = "left"
                           labelBlockPosition = "top"
                           classNameGrid      = {classNames(styles.grid_frame_top)}>
                {renderCategories()}
              </WidgetFrame>

              <WidgetFrame name               = {
                                                  <div className={classNames(styles.magicWandBlock)}>
                                                    <span>Mots-clés :</span>
                                                    <Trigger iconName = "magicWand"
                                                             onClick  = {handleMagicWandClick} />
                                                  </div>
                                                }
                           error              = ""
                           labelTextPosition  = "left"
                           labelBlockPosition = "top"
                           classNameGrid      = {classNames(styles.grid_frame_top)}>
                <TagInput availableTags   = {keywordsDictionary} 
				            initialTags     = {usedKeywords} 
							onTagListChange = {handleTagListChange} 
							onTagCreate     = {handleTagCreate} />
              </WidgetFrame>

            </div>
            )}
          </div>

          {guided && (
            <div className={classNames(styles.block)} style={optionStyle('keywords')}>
              <TagInput availableTags   = {keywordsDictionary}
                        initialTags     = {usedKeywords}
                        onTagListChange = {handleTagListChange}
                        onTagCreate     = {handleTagCreate} />
            </div>
          )}

          {/* Réseaux du compte connecté : rien à publier pour le créateur d'une vidéo depuis un autre compte. */}
          <div className={classNames(styles.block)} style={optionStyle('networks', ownerAdminId && ownerAdminId !== authentication.admin?.id ? { display: 'none' } : undefined)}>
            <div className={classNames(styles.blockTitle)}>Publier sur les réseaux</div>

            {
             (() => {
              // Deux réseaux par ligne : Youtube + Tiktok, puis Facebook + Instagram,
              // puis le reste (Linkedin, Vimeo) dans l'ordre où l'API les renvoie.
              // Dailymotion n'est pas proposé côté fiche vidéo.
              const DISPLAY_ORDER = ['youtube', 'tiktok', 'facebook', 'instagram'];
              const orderedNetworks = connectedSocialNetworks
                .filter(network => network.code !== 'dailymotion')
                .slice()
                .sort((a, b) => {
                  const indexA = DISPLAY_ORDER.indexOf(a.code);
                  const indexB = DISPLAY_ORDER.indexOf(b.code);
                  return (indexA === -1 ? DISPLAY_ORDER.length : indexA) - (indexB === -1 ? DISPLAY_ORDER.length : indexB);
                });

              const renderNetwork = (network: AdminSocialNetworkStatus) => {
               // En création (pas encore d'id), rien n'a été publié : le switch reflète
               // l'intention locale (socialNetworksToPublishAtCreation), pas un
               // enregistrement de publication — voir handleSocialNetworkToggle et
               // handleSaveButton pour la suite (envoyée à createVideo, consommée par
               // l'API une fois le transcodage terminé).
               const publication = latestPublicationFor(network.code);
               const locked      = !!publication && SOCIAL_NETWORK_LOCKED_STATUSES.includes(publication.status);
               const pendingAtCreation = !id && !!socialNetworksToPublishAtCreation[network.code];
               const checked = id ? locked : pendingAtCreation;

               const statusText =
                 !network.implemented        ? 'Bientôt disponible'                    :
                 !network.connected          ? ''                                       :
                 // Affiché en bulle au survol du switch (pendingAtCreation), pas en texte
                 // de statut — voir showPendingPublishBubble.
                 !id                         ? ''                                       :
                 // Message masqué pour le moment (demande explicite) — la vidéo non
                 // transcodée reste sans texte de statut ; le toggle reste readOnly.
                 !initialVideo.transcoded    ? ''                                       :
                 publication?.status === 'Processing' ? 'Publication en cours…'         :
                 publication?.status === 'New'         ? 'Publication en attente…'      :
                 publication?.status === 'Success'     ? 'Publié'                       :
                 publication?.status === 'Error'       ? `Échec : ${publication.errorMessage ?? ''}` :
                 '';

               // Choix de visibilité propres à ce réseau (pour l'instant les 3
               // valeurs Youtube — chaque réseau aura les siens le jour où il sera
               // implémenté). Choisi avant de publier ; figé une fois verrouillé.
               const privacyChoices = SOCIAL_NETWORK_PRIVACY_STATUS_CHOICES_BY_CODE[network.code] ?? [];
               const privacyValue   = socialNetworkPrivacyStatusByCode[network.code] ?? privacyChoices[0]?.value ?? 'private';
               const showPrivacySelect = !locked && privacyChoices.length > 0;
               const statusContent = publication?.status === 'Success' && publication.externalUrl
                 ? <a href={publication.externalUrl} target="_blank" rel="noopener noreferrer">{statusText}</a>
                 : <span className={classNames(styles.text)}>{statusText}</span>;

               const logo = SOCIAL_NETWORK_LOGO_BY_CODE[network.code];

               return (
                <WidgetFrame key                = {network.code}
                             name               = {logo
                               ? <img src={logo} alt={network.name} title={network.name} style={{ height: 25, width: 'auto', display: 'block' }} />
                               : `${network.name} :`}
                             error              = ""
                             labelTextPosition  = {logo ? "center" : "left"}
                             labelBlockPosition = "left"
                             labelVerticalAlign = "center"
                             childrenVerticalAlign = "center"
                             labelWidth         = {logo ? '40px' : '115px'}
                             classNameGrid      = {classNames(styles.grid_frame)}>
                  <div className={styles.row} style={{ gap: 12, width: '100%', flexWrap: 'wrap' }}>
                    {/* Réseau non configuré : switch gris, et un clic explique quoi faire.
                        Le Toggle en lecture seule n'émet rien, mais le clic remonte jusqu'ici.
                        En création, coché : la bulle explique pourquoi rien ne s'affiche encore. */}
                    <div onClick      = {!network.connected ? (e: React.MouseEvent<HTMLDivElement>) => showNotConfiguredBubble(e.currentTarget) : undefined}
                         onMouseEnter = {pendingAtCreation ? (e: React.MouseEvent<HTMLDivElement>) => showPendingPublishBubble(e.currentTarget) : undefined}
                         onMouseLeave = {pendingAtCreation ? () => setPendingPublishBubble(null) : undefined}
                         style        = {{ display: 'inline-flex', flex: 'none' }}>
                      <Toggle checked      = {checked}
                              readOnly     = {!network.implemented || !network.connected || (id ? (!initialVideo.transcoded || locked) : false)}
                              form         = "switch"
                              fillColorOff = {!network.connected ? '#6b7280' : undefined}
                              onChange     = {(newState: boolean) => handleSocialNetworkToggle(network, newState)} />
                    </div>
                    {
                     showPrivacySelect &&
                     <div style={{ flex: '1 1 0', minWidth: 0 }}>
                       <Select choices         = {privacyChoices.map(c => c.value)}
                               choicesLabel    = {privacyChoices.map(c => c.label)}
                               value           = {privacyValue}
                               onChange        = {(e: React.ChangeEvent<HTMLSelectElement>) => handleSocialNetworkPrivacyStatusChange(network.code, e.target.value as SocialNetworkPrivacyStatus)}
                               showPlaceholder = {false}
                               width           = "100%" />
                     </div>
                    }
                    {
                     // Pas de menu (réseau pas encore disponible, publication verrouillée) : le
                     // statut prend la place du menu, sur la ligne du switch, avec le même retrait
                     // que le texte à l'intérieur d'un menu.
                     !showPrivacySelect && statusText !== '' &&
                     <div style={{ flex: '1 1 0', minWidth: 0, paddingLeft: 10 }}>
                       {statusContent}
                     </div>
                    }
                    {
                     // Engrenage sur tout réseau disponible : un compte non connecté se connecte,
                     // un compte connecté peut être déconnecté (pour en lier un autre, par exemple).
                     network.implemented &&
                     <div onClick   = {() => network.connected ? setSocialNetworkToDisconnect(network) : handleConnectSocialNetwork(network.code)}
                          title     = {network.connected ? "Déconnecter ce compte" : "Connecter ce compte"}
                          role      = "button"
                          tabIndex  = {0}
                          onKeyDown = {(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); network.connected ? setSocialNetworkToDisconnect(network) : handleConnectSocialNetwork(network.code); } }}
                          style     = {{ display: 'inline-flex', cursor: 'pointer', marginLeft: 'auto', flex: 'none' }}>
                       <Icon name       = "gear"
                             color      = "var(--color-Field-Icon-normal)"
                             hoverColor = "var(--color-Field-Icon-hover-normal)"
                             width      = {18}
                             height     = {18} />
                     </div>
                    }
                    {
                     // Menu affiché : le statut passe sur sa propre ligne, pour ne pas rogner le menu
                     // (qui garde ainsi la même largeur d'un réseau à l'autre).
                     showPrivacySelect && statusText !== '' &&
                     <div style={{ flexBasis: '100%', paddingLeft: 10 }}>
                       {statusContent}
                     </div>
                    }
                  </div>
                </WidgetFrame>
               );
              };

              const rows: AdminSocialNetworkStatus[][] = [];
              for (let i = 0; i < orderedNetworks.length; i += 2) {
               rows.push(orderedNetworks.slice(i, i + 2));
              }

              return rows.map((row, rowIndex) => (
               <div key={rowIndex} style={{ display: 'flex', gap: 12, width: '100%' }}>
                 {row.map((network, columnIndex) => (
                  <div key       = {network.code}
                       className = {classNames(columnIndex < row.length - 1 && styles.socialNetworkDivider)}
                       style     = {{ flex: '1 1 0', minWidth: 0 }}>
                    {renderNetwork(network)}
                  </div>
                 ))}
               </div>
              ));
             })()
            }
            {notConfiguredBubble && createPortal(
              <div style={{
                position: 'fixed', zIndex: 10001,
                top: notConfiguredBubble.top, left: notConfiguredBubble.left,
                transform: 'translateY(-100%)',
                background: '#1e293b', color: '#e2e8f0',
                fontSize: 12, fontWeight: 600,
                padding: '5px 10px', borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                whiteSpace: 'nowrap', pointerEvents: 'none',
              }}>
                Cliquer sur l'engrenage pour connecter ce compte
              </div>,
              document.body
            )}
            {pendingPublishBubble && createPortal(
              <div style={{
                position: 'fixed', zIndex: 10001,
                top: pendingPublishBubble.top, left: pendingPublishBubble.left,
                transform: 'translateY(-100%)',
                background: '#1e293b', color: '#e2e8f0',
                fontSize: 12, fontWeight: 600,
                padding: '5px 10px', borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                whiteSpace: 'nowrap', pointerEvents: 'none',
              }}>
                Sera publiée après la création
              </div>,
              document.body
            )}
          </div>

          {/* ===== Ligne 3 : Accès et monétisation / Audience (2 colonnes) ===== */}
          <div className={classNames(styles.block)} style={optionStyle('access')}>
            <div className={classNames(styles.blockTitle)}>Accès et monétisation</div>

            <WidgetFrame name               = "Statut requis :"
                         error              = {requiredUserStatusIdErrorMessage}
                         labelTextPosition  = "left"
                         labelBlockPosition = "top"
						   classNameGrid      = {classNames(styles.grid_frame)}>
<div className={styles.row}>
  <Select
      choices={requiredStatusChoices}
      choicesLabel={requiredStatusLabels}
      value={`${requiredUserStatusId}`}
      onChange={handleRequiredUserStatusSelectChange}
      showPlaceholder={false}
      width="100%"
  />

  {isPaidStatus && (
      <div className={styles.field}>
          <span>Prix TTC :</span>
          <Field
              type="number"
              decimalPlaces={2}
              value={soldProductPrice}
              onChange={handleSoldProductPriceChange}
              width="100px"
          />
          <span>€</span>
      </div>
  )}
</div>

            </WidgetFrame>

          </div>

          {/* Audience : étalée sur les colonnes 2 et 3 de la grille (grid-column
              2/4), à côté d'« Accès et monétisation » qui garde la colonne 1. */}
          <div className={classNames(styles.block)} style={guided ? { display: 'none' } : { gridColumn: '2 / 4' }}>
            <div className={classNames(styles.blockTitle)}>Audience</div>

            <WidgetFrame name               = "Identifiant Youtube :"
			             error              = {youtubeIdErrorMessage}
						 labelTextPosition  = "left"
						 labelBlockPosition = "left"
						 classNameGrid      = {classNames(styles.grid_frame)}>
              <Field type         = "text"
				     placeholder  = ""
					 value        = {youtubeId}
					 onChange     = {handleYoutubeIdChange} />
            </WidgetFrame>

            <div className={styles.row} style={{ gap: 16, width: '100%', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <WidgetFrame name               = "Vues Youtube :"
                             error              = ""
							 labelTextPosition  = "left"
							 labelBlockPosition = "left"
							 labelWidth         = "110px"
							 classNameGrid      = {classNames(styles.grid_frame)}>
                  <Field type        = "text"
				         placeholder = ""
						 readOnly    = {true}
						 value       = {`${initialVideo.numberOfViewsFromYoutube}`} />
                </WidgetFrame>
              </div>

              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <WidgetFrame name               = "Vues Kleon :"
                             error              = ""
							 labelTextPosition  = "left"
							 labelBlockPosition = "left"
							 labelWidth         = "100px"
							 classNameGrid      = {classNames(styles.grid_frame)}>
                  <Field type        = "text"
				         placeholder = ""
						 readOnly    = {true}
						 value       = {`${initialVideo.numberOfViews}`} />
                </WidgetFrame>
              </div>

              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <WidgetFrame name               = {<span title="Pouces levés" aria-label="Pouces levés" role="img">👍</span>}
                             error              = ""
							 labelTextPosition  = "left"
							 labelBlockPosition = "left"
							 labelWidth         = "30px"
							 classNameGrid      = {classNames(styles.grid_frame)}>
                  <Field type        = "text"
				         placeholder = ""
						 readOnly    = {true}
						 value       = {`${initialVideo.likeCount}`} />
                </WidgetFrame>
              </div>

              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <WidgetFrame name               = {<span title="Pouces baissés" aria-label="Pouces baissés" role="img">👎</span>}
                             error              = ""
							 labelTextPosition  = "left"
							 labelBlockPosition = "left"
							 labelWidth         = "30px"
							 classNameGrid      = {classNames(styles.grid_frame)}>
                  <Field type        = "text"
				         placeholder = ""
						 readOnly    = {true}
						 value       = {`${initialVideo.dislikeCount}`} />
                </WidgetFrame>
              </div>
            </div>
          </div>

          {
           // Bloc masqué (demande explicite).
           false &&
           <div className={classNames(styles.block)}>
            <div className={classNames(styles.blockTitle)}>Document associé</div>
            <div className={classNames(styles.documentListBlock)}>
              <DocumentList documents        = { documents }
                            onDocumentAdd    = { (doc)   => { setDocuments(prev => [...prev, doc]); } }
                            onDocumentUpdate = { (doc)   => { setDocuments(prev => prev.map(d => d.id === doc.id ? doc : d)); } }
                            onDocumentDelete = { (docId) => { setDocuments(prev => prev.filter(d => d.id !== docId)); } } />
            </div>
           </div>
          }

          {
           // Récupération Youtube : hors grille des trois lignes ci-dessus (elle n'a pas
           // de place attitrée dans le découpage demandé), pleine largeur, création uniquement.
           !isEditMode &&
             (() => {
               // Même principe que le bandeau Id/Uuid/Date et la barre de boutons plus
               // haut : projeté à côté du sélecteur de type quand MediaSheet ouvre un
               // emplacement, à sa place d'origine sinon.
               const youtubeRetrievalBlock = (
                <div className = {classNames(styles.block)} style = {guided ? optionStyle('youtube') : { height: '100%', justifyContent: 'center', gridColumn: '1 / 4' }}>
                  <div className = {classNames(styles.creation_from_Youtube_titleRow)}>
                    <span className = {classNames(styles.creation_from_Youtube_title)}>
                      Récupération des informations de la vidéo Youtube
                    </span>
                    <div className = {classNames(styles.creation_from_Youtube_field)}>
                      <Field type        = "text"
                             placeholder = ""
                             value       = {youtubeIdForRetrieving}
                             onChange    = {handleYoutubeIdForRetrievingChange} />
                    </div>
                    <Trigger name    = "Récupérer"
                             variant = "flat"
                             onClick = {handleInformationsRetrieveFromYoutubeClick} />
                  </div>
                </div>
               );

               return youtubeRetrievalSlot ? createPortal(youtubeRetrievalBlock, youtubeRetrievalSlot) : youtubeRetrievalBlock;
             })()
          }

        </div>

        {
         // Le même contenu, à deux endroits possibles : dans l'emplacement
         // ouvert par MediaSheet à droite du sélecteur de type quand il existe,
         // en bas du formulaire sinon. Le portail permet de le POSITIONNER chez
         // le parent sans le sortir d'ici : les gestionnaires de clic restent
         // ceux de cette fiche, et l'arbre React est inchangé.
         (() => {
           const rappel = numberOfModifiedFields > 0 && isEditMode && (
             <p className={styles.modBadge}>
               {numberOfModifiedFields} modification{numberOfModifiedFields > 1 ? "s" : ""}
             </p>
           );

           const boutons = (
             <div className={classNames(styles.block_buttons)}>
               <Trigger name="Annuler" onClick={handleCancelButton3} variant="flat" />
               <Trigger {...(isEditMode && numberOfModifiedFields > 0 ? { buttonBackgroundColor: "#DC2626", buttonBackgroundHoverColor: "#B91C1C", buttonForegroundColor: "#fff", buttonForegroundHoverColor: "#fff" } : {})} name={isEditMode ? `Enregistrer${numberOfModifiedFields > 0 ? ` (${numberOfModifiedFields})` : ""}` : "Créer"} onClick={handleSaveButton} variant="flat" />
             </div>
           );

           const actionButtons = (
             <div className={classNames(actionsSlot ? styles.block_buttons_in_header : styles.block_buttons_and_message)}>
               {/* Dans l'en-tête, le rappel passe AVANT les boutons : la barre est
                   collée à droite, et l'insérer après décalerait « Annuler » et
                   « Enregistrer » à chaque apparition — des commandes qui bougent
                   sous le curseur. En bas du formulaire l'ordre historique est
                   conservé, le bloc y est centré. */}
               {actionsSlot ? <>{rappel}{boutons}</> : <>{boutons}{rappel}</>}
             </div>
           );

           return actionsSlot ? createPortal(actionButtons, actionsSlot) : actionButtons;
         })()
        }

      </div>
    </div>
  );
};

export default VideoSheet2;
